import React, { useState, useEffect } from 'react';
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadFull } from "tsparticles"; 
import { 
    BarChart, 
    Bar, 
    XAxis, 
    YAxis, 
    Tooltip, 
    Legend, 
    CartesianGrid, 
    ResponsiveContainer, 
    ScatterChart, 
    Scatter, 
    ZAxis
} from 'recharts';
import { 
    Home, 
    User, 
    Briefcase, 
    Mail, 
    Menu, 
    X, 
    Github, 
    Linkedin, 
    ExternalLink, 
    Brain, 
    Database, 
    BookOpen, 
    ArrowUpRight, 
    Loader2, 
    BarChart2, 
    Sun, 
    Moon, 
    Clock, 
    Zap, 
    Scale, 
    TrendingUp, 
    ChevronDown, 
    GraduationCap, 
    Award, 
    Trophy, 
    Calendar, 
    ChevronUp,
    Download,
    FileText,
    Scroll,        
    FlaskConical,
    Youtube,
    Star,
    Copy,  
    Check
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// ==========================================
// 1. CONFIGURATION & CONSTANTS
// ==========================================
const SPRING_CONFIG = { type: "spring", stiffness: 150, damping: 20 };
const PROJECTS_PER_PAGE = 6;
const CERTIFICATIONS_PER_PAGE = 3;
const COMPETITIONS_PER_PAGE = 4;
// const TESTIMONIALS_PER_PAGE = 3;

// ==========================================
// 2. PERSONALIZATION & HELPER FUNCTIONS
// ==========================================

const PERSONAL_INFO = {
    name: "Masuddar Rahaman",
    email: "masuddarrahaman31@gmail.com",
    linkedin: "https://www.linkedin.com/in/masuddar-rahaman-b5044b283/",
    github: "https://github.com/Masuddar",
    blogs: "https://masuddar.in/",
    portfolio: "https://masuddar.netlify.app/",
    location: "Siliguri, West Bengal",
    resumeUrl: "https://drive.google.com/file/d/1bB5yK2z8Ct36VDAnC_Emkipp9XDh8Gub/view?usp=sharing" 
};

// --- HELPER: Parse Markdown Bold (**text**) to HTML ---
const renderText = (text) => {
    if (!text) return "";
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={index} className="font-bold text-blue-500 dark:text-blue-400">{part.slice(2, -2)}</strong>;
        }
        return part;
    });
};

// ==========================================
// MOCK DATA: CHART DATA & CATEGORIES
// ==========================================

// 1. Chart Data for Fake News / Spam Projects
const project1ChartData = [
  { name: 'LR', accuracy: 0.9791, precision: 0.98 },
  { name: 'XGBoost', accuracy: 0.985, precision: 0.982 },
  { name: 'Best Model', accuracy: 0.99, precision: 0.989 },
];

// 2. Chart Data for EDA Projects
const project2ChartData = [
  { x: 100, y: 200, z: 200, cluster: 'Low-Value' },
  { x: 170, y: 300, z: 400, cluster: 'High-Value' },
  { x: 140, y: 250, z: 280, cluster: 'Mid-Value' },
  { x: 110, y: 280, z: 200, cluster: 'Low-Value' },
];

// 3. Project Filter Categories
const PROJECT_CATEGORIES = [
    "All", 
    "NLP/Classification", 
    "Deep Learning", 
    "Visualization/EDA", 
    "Clustering/NLP", 
    "Time Series"
];

// ==========================================
// 3. PROJECT DATA
// ==========================================

const projectData = [
    // 1. Multilingual Language Detection (Ongoing)
    // {
    //      id: "multilingual-detection",
    //      title: "Multilingual Language Detection",
    //      description: "Ongoing research project using Hybrid NLP & DL architectures to detect languages in mixed-script text.",
    //      problem: "Classifying code-mixed text (e.g., Hinglish) on social media is difficult for standard models due to script mixing.",
    //      methodology: "Developing a BERT-based architecture fine-tuned on custom datasets. Utilizing character-level embeddings combined with contextual word embeddings.",
    //      category: "NLP/Classification",
    //      tags: ["Python", "BERT", "Deep Learning", "NLP", "Research"],
    //      githubUrl: "#", 
    //      liveUrl: "#", // No live link yet
    //      chartData: null,
    //      image: "https://placehold.co/600x400/7c3aed/ffffff?text=Research:+Multilingual+NLP",
    //      stats: { linesOfCode: "In Progress", timeSpent: "Ongoing", impact: "Research Paper" }
    // },
    // 2. Spam Detection
{
        id: "spam-detection",
        title: "Email/SMS Spam Detector",
        description: "A real-time machine learning web application capable of filtering spam messages with high precision using an automated NLP pipeline.",
        problem: "Inbox clutter and security risks caused by unsolicited spam messages.",
        methodology: "Engineered a TF-IDF + Multinomial Naive Bayes classification pipeline. Serialized the model/vectorizer using Pickle and deployed an interactive frontend via Streamlit.",
        category: "NLP/Web App",
        tags: ["Python", "Scikit-learn", "Streamlit", "TF-IDF", "Multinomial NB"],
        githubUrl: "https://github.com/Masuddar/Spam-Mail-Detection",
        liveUrl: "https://spam-mail-detection-masu.streamlit.app/",
        chartData: project1ChartData, 
        chartType: "bar",
        image: "https://i.postimg.cc/XvbxZDTv/image.png",
        stats: { linesOfCode: "1.2K", timeSpent: "Nov 2025", impact: "98% Accuracy" }
    },
    // 3. Movie Recommendation System
    {
        id: "movie-recommender",
        title: "Movie Recommendation System",
        description: "Content-based recommender system utilizing Python and scikit-learn to suggest movies based on user preferences.",
        problem: "Users struggle to find movies similar to their specific tastes among thousands of options.",
        methodology: "Processed the TMDB dataset. Used Cosine Similarity on vectors created from genres, cast, and directors to recommend the top 5 similar movies.",
        category: "Clustering/NLP",
        tags: ["Python", "Scikit-learn", "Streamlit", "Cosine Similarity", "Pandas"],
        githubUrl: "https://github.com/Masuddar/Movie_recommendation-_system",
        liveUrl: "https://www.linkedin.com/feed/update/urn:li:activity:7270716406541803520/",
        chartData: null,
        image: "https://i.postimg.cc/5N6jW4K6/image.png",
        stats: { linesOfCode: "2.5K", timeSpent: "3 Weeks", impact: "Personalized UX" }
    },
    // 4. Crop Health Predictor
    {
        id: "crop-health-prediction",
        title: "Crop Health Prediction",
        description: "CNN model trained on PlantVillage dataset to accurately diagnose crop diseases from leaf images.",
        problem: "Farmers need rapid, accessible ways to identify plant diseases to prevent crop loss.",
        methodology: "Used TensorFlow and Keras to build a Convolutional Neural Network (CNN). Preprocessed images using OpenCV. Achieved high validation accuracy on the test set.",
        category: "Deep Learning",
        tags: ["Python", "TensorFlow", "Keras", "OpenCV", "CNN"],
        githubUrl: "https://github.com/Masuddar/Crop-Health-and-Advisory-System-for-Indian-Farmers-main",
        liveUrl: "#",
        chartData: null,
        image: "https://i.postimg.cc/YqyS8v7Y/image.png",
        stats: { linesOfCode: "3.0K", timeSpent: "1 Month", impact: "Agri-Tech Solution" }
    },
    // 5. Fake News Detection
    {
        id: "fake-news-detector",
        title: "Fake News Detector",
        description: "NLP and Deep Learning model to analyze text patterns and classify misinformation effectively.",
        problem: "The rapid spread of misinformation on digital platforms requires automated detection systems.",
        methodology: "Implemented LSTM (Deep Learning) layers to understand sequence context in news articles. Compared results with Logistic Regression benchmarks.",
        category: "NLP/Classification",
        tags: ["Python", "Deep Learning", "NLP", "LSTM", "TensorFlow"],
        githubUrl: "https://github.com/Masuddar/Iemhacks-3.0-2025",
        liveUrl: "https://iemhacks-30-2025-masuddar-rahaman.streamlit.app/",
        chartData: project1ChartData,
        chartType: "bar",
        image: "https://i.postimg.cc/wjhdNZbx/image.png",
        stats: { linesOfCode: "3.5K", timeSpent: "2 Weeks", impact: "Hackathon Finalist" }
    },
    // 6. Telecom Churn Analysis & EDA
    {
        id: "telecom-churn-eda",
        title: "Telecom Churn Analysis",
        description: "Visualized customer retention patterns and predicted churn behavior through interactive analytics.",
        problem: "Telecom companies need to identify at-risk customers to improve retention rates.",
        methodology: "Extensive EDA using Plotly and Seaborn. Identified key factors (contract type, monthly charges) correlated with customer churn.",
        category: "Visualization/EDA",
        tags: ["Python", "Pandas", "Seaborn", "Plotly", "EDA"],
        githubUrl: "https://github.com/Masuddar/Telcom-Customer-Churn",
        liveUrl: "https://telcom-customer-churn-masuddar.streamlit.app/",
        chartData: project2ChartData,
        chartType: "scatter",
        image: "https://i.postimg.cc/tgctjqFx/image.png",
        stats: { linesOfCode: "2.0K", timeSpent: "2 Weeks", impact: "Business Insights" }
    },
    // 7. Blinkit Power BI
    {
        id: "blinkit-dashboard",
        title: "Blinkit Dashboard (Power BI)",
        description: "Modeled data with Python and Power BI to create dashboards and visualizations for strategic decisions.",
        problem: "Raw sales data needed to be transformed into actionable insights for quick decision-making.",
        methodology: "Cleaned data using Python (Pandas). Created an interactive Power BI dashboard with slicers for region, item type, and sales performance metrics.",
        category: "Visualization/EDA",
        tags: ["Power BI", "Python", "Data Modeling", "Dashboard", "Business Intelligence"],
        githubUrl: "https://github.com/Masuddar/blinkit_e-commerce",
        liveUrl: "https://www.linkedin.com/feed/update/urn:li:activity:7277073131834372096/",
        chartData: null,
        image: "https://i.postimg.cc/ZK5Gn06V/image.png",
        stats: { linesOfCode: "N/A", timeSpent: "1 Week", impact: "Data-Driven Decisions" }
    },
    // 8. Student Performance Analysis
    {
        id: "student-performance",
        title: "Student Performance Analysis",
        description: "Visualized diverse academic trends through interactive and varied graphs using Plotly.",
        problem: "Understanding factors affecting student scores (math, reading, writing) across different demographics.",
        methodology: "Performed univariate and multivariate analysis. Created heatmaps to show correlations between parental education, preparation courses, and scores.",
        category: "Visualization/EDA",
        tags: ["Python", "Pandas", "Seaborn", "Plotly", "Statistics"],
        githubUrl: "https://github.com/Masuddar/Student-Performance-Analysis-Visualization",
        liveUrl: "https://student-performance-analysis-visualization-masuddar.streamlit.app/",
        chartData: null,
        image: "https://i.postimg.cc/MKBL342Y/image.png",
        stats: { linesOfCode: "1.5K", timeSpent: "1 Week", impact: "Academic Insights" }
    },
];

// ==========================================
// 4. MOCK DATA: SKILLS
// ==========================================

const skillsData = {
    programming: [
        { name: "Python", proficiency: 70 }, 
        { name: "SQL (PostgreSQL)", proficiency: 70 },
        { name: "Django / Flask", proficiency: 66 }, 
        { name: "HTML / CSS", proficiency: 78 },      
        { name: "C / C++", proficiency: 70 }
    ],
    ml_analysis: [
        { name: "Pandas / NumPy", proficiency: 80 }, 
        { name: "Supervised Learning ML", proficiency: 76 },
        { name: "NLP-ML", proficiency: 80 },              
        { name: "DL - Learning Basics", proficiency: 50 },
        { name: "TensorFlow / Keras - Learning Basics", proficiency: 50 },
    ],
    tools_viz: [
        { name: "Streamlit", proficiency: 88 }, 
        { name: "Power BI", proficiency: 70 }, 
        { name: "Matplotlib / Seaborn", proficiency: 80 }, 
        { name: "Plotly", proficiency: 80 },
    ],
    dev_ops: [
        { name: "Git / GitHub", proficiency: 90 }, 
        { name: "Jupyter / Colab", proficiency: 95 }, 
        { name: "ETL & Data Cleaning", proficiency: 90 }, 
        { name: "VS Code", proficiency: 90 },
        { name: "Model Deployment", proficiency: 75 }, 
    ]
};

// ==========================================
// 5. MOCK DATA: EXPERIENCE
// ==========================================

const experienceData = [
    { 
        id: "exp1",
        title: "Tech Contributor", 
        company: "GirlScript Summer of Code", 
        period: "Aug 2025 - Oct 2025", 
        type: "Open Source",
        theme: "orange", 
        image: "https://i.postimg.cc/dV2bK77n/image.png",
        proofUrl: "https://www.linkedin.com/in/masuddar-rahaman/overlay/experience/2695173376/multiple-media-viewer/?profileId=ACoAAETxdKwBzl_LwSeNVndUqjd-LBYYai3L4Oo&treasuryMediaId=1754144189666", 
        description: 
        "I feel deeply honored to have been selected as a Tech Contributor for the GirlScript Summer of Code 2025. This opportunity allowed me to immerse myself in the open-source community, learn from talented peers, and broaden my technical perspective. Although academic commitments & Hackathons limited the extent of my contributions, the experience was immensely rewarding. I remain grateful and excited to return even stronger in future editions."
    },
    { 
        id: "exp2",
        title: "Internshala Student Partner", 
        company: "Internshala", 
        period: "Jul 2025 - Sep 2025", 
        type: "Leadership",
        theme: "cyan", 
        image: "https://i.postimg.cc/fW3XqmFc/image.png",
        proofUrl: "https://www.linkedin.com/in/masuddar-rahaman/overlay/experience/2695171303/multiple-media-viewer/?profileId=ACoAAETxdKwBzl_LwSeNVndUqjd-LBYYai3L4Oo&treasuryMediaId=1754122028598", 
        description:
        "This 3-month remote leadership internship enabled me to lead outreach initiatives and drive student engagement across campuses. Acting as a link between Internshala and the student community strengthened my communication, public speaking, and team-coordination abilities. I am grateful for the impactful learning and leadership exposure this role provided."
    },
{ 
    id: "exp3",
    title: "Student Ambassador", 
    company: "1Stop.ai, Alcheringa IIT Guwahati", 
    period: "Dec 2024 - Dec 2024", 
    type: "Ambassador",
    theme: "rose", 
    image: "https://i.postimg.cc/d1KSvft2/image.png",
    proofUrl: "https://www.linkedin.com/in/masuddar-rahaman/overlay/1735542424245/single-media-viewer/?profileId=ACoAAETxdKwBzl_LwSeNVndUqjd-LBYYai3L4Oo", 
    description:
    "I am grateful for the opportunity to serve as a Campus Ambassador for Alcheringa, IIT Guwahati, in association with 1Stop.ai. During this role, I organized workshops, guided students on technical programs, and collaborated with peers to enhance campus engagement. The experience strengthened my communication, coordination, and creative problem-solving skills.\n\n**Skills:** Communication · Attention to Detail · Creative Problem Solving"
},

    { 
        id: "exp4",
        title: "Data Science Intern", 
        company: "Technophilia Solutions, IIT Kharagpur", 
        period: "Sep 2024 - Sep 2024", 
        type: "Internship",
        theme: "indigo", 
        image: "https://i.postimg.cc/vBt7Ys7Q/image.png", 
        proofUrl: "https://www.linkedin.com/in/masuddar-rahaman/overlay/1733984321132/single-media-viewer/?profileId=ACoAAETxdKwBzl_LwSeNVndUqjd-LBYYai3L4Oo", 
        description:
        "As a Data Science Intern, I worked extensively with Python libraries such as **NumPy, Pandas, Matplotlib, and Seaborn** to analyze, manipulate, and visualize datasets. I delivered structured insights through presentations and strengthened my analytical thinking as well as communication skills. This internship reinforced my interest in data-driven problem-solving and practical business intelligence.\n\n**Skills:** NumPy · Matplotlib · Scikit-Learn · Product Analytics · Statistical Data Analysis · Business Intelligence Tools"
    },
];

// ==========================================
// 6. MOCK DATA: RESEARCH
// ==========================================

const researchData = [
    {
        id: "r1",
        title: "Multilingual Language Detection using Hybrid ML, DL & Transformers",
        publication: "Research in Progress",
        type: "NLP Research", // Updated
        showcaseStatus: "Self-Driven Research Initiative", 
        date: "Present",
        image: "https://i.postimg.cc/nrzwdZ9R/image.png", 
        abstract: "Developing a high-precision language identification system tailored for **India-specific linguistic diversity**. The architecture utilizes a **Hybrid approach** combining traditional **Machine Learning** with **Deep Learning** and **Transformer models (BERT)**. This research specifically addresses the complexities of code-mixed text (e.g., Hinglish) and low-resource Indian languages, aiming to create a robust NLP pipeline for accurate classification in diverse social media streams.",
        tags: ["NLP", "Transformers", "BERT", "Hybrid ML/DL"],
        url: "#", 
        guide: {
            name: "Prof. Ankita Sinha",
            title: "Asst. Professor (CS/IT) | PhD Scholar, KIIT",
            image: "https://ui-avatars.com/api/?name=Ankita+Sinha&background=ec4899&color=fff&size=128",
            profileUrl: "https://www.linkedin.com/in/ankita-sinha-b12899151/"
        }
    },
    {
        id: "r2",
        title: "Advanced Detection of Counterfeit Banking Android Packages (APKs)",
        publication: "Patent Planned",
        type: "ML/Cybersecurity Framework", // Updated
        showcaseStatus: "Patent Planned • Showcased to SBI & MP Police",
        date: "Oct 2025",
        image: "https://i.postimg.cc/bwZtJWmw/PANA3118.jpg?q=80&w=2070&auto=format&fit=crop",
        abstract: "Presented at **NIT Bhopal** and was awarded the **National Finalist Award**.Built on the core principle that **'Prevention is better than Cure'**, this cybersecurity framework integrates **AI/ML** with robust **Cryptography** to detect malicious banking applications. Unlike traditional reactive measures, our static analysis engine proactively scans APK signatures and manifests to identify permission discrepancies. The project is currently advancing towards a patent under expert supervision, following successful demonstrations to **State Bank of India (SBI)** and **MP Police Cyber Cell** officials.",
        tags: ["Cybersecurity", "Cryptography", "AI/ML", "Fraud Detection"],
        url: "#",
        guide: {
            name: "Dr. Prasanta Kumar Roy",
            title: "PhD, IIT Dhanbad",
            image: "https://ui-avatars.com/api/?name=Prasanta+Kumar&background=0ea5e9&color=fff&size=128",
            profileUrl: "https://www.linkedin.com/in/dr-prasanta-kumar-roy-b4024978/"
        }
    },
    {
        id: "r3",
        title: "Lightweight Automated Fraud Detection for Academic Certificates",
        publication: "Patent Planned",
        type: "EdTech Security System", // Updated
        showcaseStatus: "Patent Planned • SIH Winner (1st Rank)", 
        date: "Sep 2025",
        image: "https://i.postimg.cc/bv333m1x/image.png",
        abstract: "An award-winning system (**SIH Internal Hackathon - 1st Rank**) designed as a scalable, resource-efficient alternative to Blockchain. Instead of heavy distributed ledgers, we utilize **Advanced Cryptography** combined with **ML/DL models** to verify educational credentials. This approach significantly **reduces server load** and latency while maintaining high security. A patent roadmap is currently being developed under the guidance of **Dr. Prasanta Kumar Roy** to protect the unique low-latency validation algorithm.",
        tags: ["Cryptography", "ML/DL", "EdTech", "Server Optimization"],
        url: "#",
        guide: {
            name: "Dr. Prasanta Kumar Roy",
            title: "PhD, IIT Dhanbad",
            image: "https://ui-avatars.com/api/?name=Prasanta+Kumar&background=0ea5e9&color=fff&size=128",
            profileUrl: "https://www.linkedin.com/in/dr-prasanta-kumar-roy-b4024978/"
        }
    }
];
// ==========================================
// 7. MOCK DATA: TESTIMONIALS
// ==========================================
// (COMMENTED OUT FROM UI USE, BUT KEPT FOR REFERENCE)
// Testimonials data - currently not used (Testimonials section disabled)
// const testimonialsData = [
//     {
//         id: 1,
//         quote: "Masuddar's analytical skills are top-notch. He was able to find insights in our data that others missed, directly contributing to our project's success at the hackathon.",
//         name: "Dr. A. K. Singh",
//         title: "Professor, SIT",
//         image: "https://placehold.co/100x100/e0e7ff/3b82f6?text=AS"
//     },
//     {
//         id: 2,
//         quote: "Working with Masuddar during GSSOC was fantastic. He's a proactive learner, a great collaborator, and consistently delivered high-quality code. He has a bright future in data science.",
//         name: "Priya Sharma",
//         title: "Mentor, GirlScript Foundation",
//         image: "https://placehold.co/100x100/fecaca/c026d3?text=PS"
//     },
//     {
//         id: 3,
//         quote: "His ability to build and deploy a Streamlit app for our data visualization needs was impressive. He understands how to bridge the gap between complex models and user-friendly insights.",
//         name: "Rohan Gupta",
//         title: "Project Lead, Technophilia",
//         image: "https://placehold.co/100x100/c7d2fe/1d4ed8?text=RG"
//     },
//     {
//         id: 4,
//         quote: "I was impressed by his dedication during the CIIS National Hackathon. He's a creative problem-solver who thrives under pressure. A great asset to any team.",
//         name: "Fellow Hacker",
//         title: "CIIS National Finalist",
//         image: "https://placehold.co/100x100/d1fae5/047857?text=FH"
//     }
// ];

// ==========================================
// 8. MOCK DATA: EDUCATION & CERTIFICATIONS
// ==========================================


export const educationData = [
//     {
//         id: 1,
//         institution: "IIT Madras",
//         title: "BS in Data Science & Applications",
//         period: "2025 - 2029",
//         image: "https://i.postimg.cc/02B4RY2f/image.png",
//         details: [
//             "Data Science & Machine Learning",
//             "Statistics & Probability",
//             "Mathematics & Computing"
//         ],
//         tags: ["Student", "Research"],
//         cgpa: "8.33/10" 
//     },
    {
        id: 2,
        institution: "Siliguri Institute of Technology",
        title: "B.Tech in Information Technology",
        period: "2023 - 2027",
        image: "https://i.postimg.cc/pTH7cPVK/image.png",
        details: [
            "Information Technology",
            "Computer Science",
            "Core Engineering Principles"
        ],
        tags: ["Student", "Research"],
        cgpa: "7.24/10" 
    }
];

// Certification Data
const certificationData = [
    { name: "Python for Data Science", provider: "IIT Madras", date: "Aug, 2025", url: "https://nptel.ac.in/noc/E_Certificate/NPTEL25CS104S34730002109136362", image: "https://i.postimg.cc/3JYcGCGD/Screenshot-2-11-2025-175422-www-linkedin-com.jpg" },
    { name: "Data Science: Machine Learning", provider: "HarvardX", date: "April, 2025", url: "https://courses.edx.org/certificates/572a155f577341c6a3e439b31429ca33", image: "https://i.postimg.cc/Mpn9SsR0/cer.jpg" },
    { name: "Data Engineering Professional Certificate", provider: "IBM", date: "June, 2024", url: "https://www.coursera.org/account/accomplishments/professional-cert/2USH5H9X8M8A", image: "https://i.postimg.cc/y87vJVwS/cer-1.jpg" },
    { name: "Data Analysis", provider: "Microsoft & LinkedIn", date: "Nov, 2024", url: "https://www.linkedin.com/learning/certificates/e019ad60898b60817701136ebb70ca8d169d65b285fefc327ac16211c9d88eaf?trk=share_certificate", image: "https://i.postimg.cc/rwHYR6cr/cer-4.jpg" },
    { name: "SQL for Data Science with Python", provider: "IBM", date: "July, 2024", url: "https://coursera.org/verify/QWU73HCWXL3P", image: "https://i.postimg.cc/R0vV6B9x/image.png" },
    { name: "Data Analytics & Visualization", provider: "Accenture (Job Simu.)", date: "Nov, 2024", url: "https://forage-uploads-prod.s3.amazonaws.com/completion-certificates/T6kdcdKSTfg2aotxT/hzmoNKtzvAzXsEqx8_T6kdcdKSTfg2aotxT_mGpWLYx439qmJ4oyb_1731853853511_completion_certificate.pdf", image: "https://i.postimg.cc/RCYFPy1g/image.png" },
    { name: "Accounting for Decision Making", provider: "University of Michigan", date: "Nov, 2024", url: "https://coursera.org/verify/9PVTQ9RP9YNH", image: "https://i.postimg.cc/Dw7BW2tX/cer-3.jpg" },
];

// ==========================================
// 9. MOCK DATA: COMPETITIONS
// ==========================================
const competitionData = [
    { 
        name: "Smart India Hackathon", 
        rank: "1st Place (College)", 
        details: "Winner at the college level internal hackathon.", 
        date: "2025", 
        image: "https://i.postimg.cc/85tyyNrc/image.png" 
    },
    { 
        name: "CIIS National Hackathon", 
        rank: "National Finalist", 
        details: "Selected for Grand Finale at NIT Bhopal.", 
        date: "2025", 
        image: "https://i.postimg.cc/tgDtbDRW/image.png" 
    },
    { 
        name: "Fake News Detector", 
        rank: "Top 7 Nationwide", 
        details: "IIIT Kottayam Hackathon Achievement.", 
        date: "Mar 06, 2025", 
        image: "https://i.postimg.cc/FHMwcr0Z/image.png",
        videoUrl: "https://www.youtube.com/watch?reload=9&v=MmKO5bxPjMA" // Added YouTube Link
    },
    {
        name: "DSA Coding Challenge",
        rank: "Rank 144 / 5,725",
        details: "Let's Code Community. Top 2.5% performers. **144th out of 5,725 participants**.",
        date: "May 27, 2025",
        image: "https://i.postimg.cc/SxKdTwfY/image.png"
    },
    { 
        name: "ITAC - Pravah Hackathon", 
        rank: "Top 10 Nationwide", 
        details: "Organized by **NIT Agartala**. Team **Crescent Code**. Theme: Viksit Bharat through Viksit North-East.", 
        date: "Mar 19, 2025", 
        image: "https://i.postimg.cc/sXH0Kst2/image.png" 
    }
];

// ==========================================
// 10. MOCK DATA: BLOGS
// ==========================================
const blogData = [
    { 
        title: "Gemini AI: Google's Revolutionary Multimodal Assistant in 2025", 
        date: "Jul 14, 2025", 
        snippet: "Discover how Gemini AI is reshaping industries in 2025. Complete analysis of capabilities, real-world applications, and comparison with other AI models.", 
        url: "https://masuddar.in/posts/gemini-ai-next-generation-google-model/", 
        readTime: "2 min read",
        image: "https://miro.medium.com/v2/resize:fit:686/1*5UvjWbxaDZCuTpQs9SjOhw.jpeg"
    },
    { 
        title: "Blackbox AI in 2025: Revolutionizing Coding with AI-Powered Assistance", 
        date: "Jan 30, 2025", 
        snippet: "Discover how Blackbox AI transforms coding workflows in 2025. Explore features, pricing, and benefits of this AI-driven development tool.", 
        url: "https://masuddar.in/posts/blackblox-ai-2025/", 
        readTime: "3 min read",
        image: "https://images.pexels.com/photos/577585/pexels-photo-577585.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
    },
    { 
        title: "Perplexity Ai: The Next-Gen Search Engine Revolutionizing Information Access In 2025", 
        date: "Jan 28, 2025", 
        snippet: "Discover how Perplexity AI outperforms traditional search engines with real-time answers, cited sources, and AI-powered analysis. Learn about its unique features...", 
        url: "https://masuddar.in/posts/Perplexity-AI-The-Next-Gen-Search-Engine-Revolutionizing-Information-Access-in-2025/", 
        readTime: "4 min read",
        image: "https://images.pexels.com/photos/4389795/pexels-photo-4389795.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
    },
    { }, 
];

// ==========================================
// 11. UI COMPONENTS: UTILITIES
// ==========================================

const CustomCursor = () => {
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isHovering, setIsHovering] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        if (window.matchMedia("(pointer: coarse)").matches) {
            setIsMobile(true);
            return;
        }

        const updatePosition = (e) => {
            setPosition({ x: e.clientX, y: e.clientY });
        };

        const handleMouseOver = (e) => {
            if (e.target.tagName === 'A' || e.target.tagName === 'BUTTON' || e.target.closest('[role="button"]') || e.target.closest('.cursor-pointer')) {
                setIsHovering(true);
            }
        };

        const handleMouseOut = () => {
            setIsHovering(false);
        };

        window.addEventListener('mousemove', updatePosition);
        document.addEventListener('mouseover', handleMouseOver);
        document.addEventListener('mouseout', handleMouseOut);

        return () => {
            window.removeEventListener('mousemove', updatePosition);
            document.removeEventListener('mouseover', handleMouseOver);
            document.removeEventListener('mouseout', handleMouseOut);
        };
    }, []);

    const cursorVariants = {
        default: { scale: 1, backgroundColor: "transparent", border: "2px solid #3b82f6" },
        hovering: { scale: 2.5, backgroundColor: "rgba(59, 130, 246, 0.2)", border: "2px solid rgba(59, 130, 246, 0)" }
    };

    const dotVariants = {
        default: { scale: 1 },
        hovering: { scale: 0.4 }
    };

    if (isMobile) return null;

    return (
        <>
            <motion.div
                variants={cursorVariants}
                animate={isHovering ? "hovering" : "default"}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                className="fixed top-0 left-0 w-8 h-8 rounded-full z-[9999] pointer-events-none"
                style={{ x: position.x - 16, y: position.y - 16 }}
            />
            <motion.div
                variants={dotVariants}
                animate={isHovering ? "hovering" : "default"}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
                className="fixed top-0 left-0 w-2 h-2 rounded-full bg-blue-600 z-[9999] pointer-events-none"
                style={{ x: position.x - 4, y: position.y - 4 }}
            />
        </>
    );
};

const Preloader = () => (
    <motion.div
        key="preloader"
        initial={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.8, ease: "easeInOut" } }}
        className="fixed inset-0 z-[9998] flex items-center justify-center bg-gray-900"
    >
        <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: [0, 1, 1, 0], scale: [0.8, 1.1, 1.1, 0.8] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", times: [0, 0.2, 0.8, 1] }}
            className="text-8xl font-bold text-blue-500"
        >
            MR<span className='text-4xl text-gray-400'>.DS</span>
        </motion.div>
    </motion.div>
);

const ScrollToTopButton = ({ isDarkMode }) => {
    const [isVisible, setIsVisible] = useState(false);

    const toggleVisibility = () => {
        if (window.pageYOffset > 300) {
            setIsVisible(true);
        } else {
            setIsVisible(false);
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', toggleVisibility);
        return () => window.removeEventListener('scroll', toggleVisibility);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.button
                    initial={{ opacity: 0, scale: 0.8, y: 50 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.8, y: 50 }}
                    transition={SPRING_CONFIG}
                    onClick={scrollToTop}
                    className={`fixed bottom-8 right-8 z-50 p-4 rounded-full shadow-lg ${
                        isDarkMode ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                    aria-label="Scroll to top"
                >
                    <ChevronUp size={24} />
                </motion.button>
            )}
        </AnimatePresence>
    );
};

const DotNav = ({ navItems, activeSection, scrollToSection }) => {
    return (
        <motion.nav 
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8, ...SPRING_CONFIG }}
            className="fixed top-1/2 -translate-y-1/2 right-6 z-50 hidden md:flex flex-col space-y-3"
        >
            {navItems.map(item => (
                <a
                    key={item.id}
                    href={`#${item.id}`}
                    onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(item.id);
                    }}
                    className="relative flex items-center group cursor-pointer"
                    role="button"
                    aria-label={`Scroll to ${item.name}`}
                >
                    <motion.div 
                        initial={{ opacity: 0, x: 10 }}
                        whileHover={{ opacity: 1, x: 0 }}
                        transition={{ ...SPRING_CONFIG, duration: 0.3 }}
                        className="absolute right-full mr-4 px-3 py-1.5 bg-gray-900 text-white text-sm font-semibold rounded-md whitespace-nowrap"
                    >
                        {item.name}
                        <div className="absolute right-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                    </motion.div>
                    <motion.div 
                        className="w-3 h-3 rounded-full transition-colors duration-300"
                        animate={{ 
                            scale: activeSection === item.id ? 1.5 : 1,
                            backgroundColor: activeSection === item.id ? '#3b82f6' : '#9ca3af'
                        }}
                        whileHover={{ scale: 1.5 }}
                        transition={SPRING_CONFIG}
                    />
                </a>
            ))}
        </motion.nav>
    );
};

const SkillProgress = ({ name, proficiency, isDarkMode }) => (
    <motion.div 
        className="w-full"
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.8 }}
        transition={SPRING_CONFIG}
    >
        <div className="flex justify-between mb-1 text-sm">
            <span className={isDarkMode ? "text-gray-300 font-medium" : "text-gray-800 font-medium"}>{name}</span>
            <span className="text-blue-500 font-semibold">{proficiency}%</span>
        </div>
        <div className={`w-full h-2.5 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <motion.div
                initial={{ width: "0%" }}
                whileInView={{ width: `${proficiency}%` }}
                viewport={{ once: true, amount: 0.8 }}
                transition={{ ...SPRING_CONFIG, duration: 1.5, delay: 0.2 }}
                className="h-2.5 bg-blue-600 rounded-full shadow-lg shadow-blue-500/50"
            />
        </div>
    </motion.div>
);

const ThemeToggle = ({ isDarkMode, toggleDarkMode }) => (
    <motion.button
        onClick={toggleDarkMode}
        className={`p-3 rounded-full transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-700 text-yellow-300 hover:bg-gray-600' : 'bg-gray-100 text-blue-600 hover:bg-gray-200'
        }`}
        aria-label="Toggle Dark Mode"
        whileHover={{ scale: 1.1, rotate: 10 }}
        whileTap={{ scale: 0.9 }}
    >
        <AnimatePresence mode="wait" initial={false}>
            {isDarkMode ? (
                <motion.div
                    key="moon"
                    initial={{ scale: 0, rotate: 90 }}
                    animate={{ scale: 1, rotate: 0 }}
                    exit={{ scale: 0, rotate: -90 }}
                    transition={SPRING_CONFIG}
                >
                    <Moon size={20} />
                </motion.div>
            ) : (
                <motion.div
                    key="sun"
                    initial={{ scale: 0, rotate: 90 }}
                    animate={{ scale: 1, rotate: 0 }}
                    exit={{ scale: 0, rotate: -90 }}
                    transition={SPRING_CONFIG}
                >
                    <Sun size={20} />
                </motion.div>
            )}
        </AnimatePresence>
    </motion.button>
);

const particleConfig = (isDarkMode) => ({
    background: { color: { value: "transparent" } },
    fpsLimit: 60,
    interactivity: {
        events: {
            onClick: { enable: true, mode: "push" },
            onHover: { enable: true, mode: "repulse" },
        },
        modes: {
            push: { quantity: 2 },
            repulse: { distance: 100, duration: 0.4 },
        },
    },
    particles: {
        color: { value: isDarkMode ? "#3b82f6" : "#2563eb" },
        links: {
            color: isDarkMode ? "#374151" : "#d1d5db",
            distance: 150,
            enable: true,
            opacity: 0.2,
            width: 1,
        },
        collisions: { enable: true },
        move: {
            direction: "none",
            enable: true,
            outModes: { default: "bounce" },
            random: false,
            speed: 1,
            straight: false,
        },
        number: {
            density: { enable: true, area: 800 },
            value: 60,
        },
        opacity: { value: 0.2 },
        shape: { type: "circle" },
        size: { value: { min: 1, max: 3 } },
    },
    detectRetina: true,
});

const SECTION_TITLES = {
  home: "Masuddar Rahaman | Data Scientist",
  about: "Masuddar Rahaman | About Me",
  experience: "Masuddar Rahaman | Experience",
  research: "Masuddar Rahaman | Research", 
  testimonials: "Masuddar Rahaman | Testimonials",
  education: "Masuddar Rahaman | Education",
  projects: "Masuddar Rahaman | Projects",
  blog: "Masuddar Rahaman | Blog",
  contact: "Masuddar Rahaman | Contact",
};

// ==========================================
// 12. MAIN APP COMPONENT
// ==========================================

// --- MAIN APP COMPONENT ---

export default function App() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('home');
    const [isDarkMode, setIsDarkMode] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [particlesInit, setParticlesInit] = useState(false);

    useEffect(() => {
        initParticlesEngine(async (engine) => {
            await loadFull(engine);
        }).then(() => {
            setParticlesInit(true);
        });
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 2000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            @media (pointer: fine) {
                * { cursor: none !important; }
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const toggleDarkMode = () => setIsDarkMode(prev => !prev);

    const scrollToSection = (sectionId) => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
        setActiveSection(sectionId);
        setIsMenuOpen(false);
    };

    useEffect(() => {
        if (isLoading) return;
        // Removed testimonials from scroll spy
        const sections = ['home', 'about', 'experience', 'research', 'education', 'projects', 'blog', 'contact'];
        const handleScroll = () => {
            let currentActive = 'home';
            for (const sectionId of sections) {
                const element = document.getElementById(sectionId);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    if (rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2) {
                        currentActive = sectionId;
                        break;
                    }
                }
            }
            setActiveSection(prevActive => {
                if (prevActive !== currentActive) {
                    document.title = SECTION_TITLES[currentActive] || "Masuddar Rahaman | Data Scientist";
                    return currentActive;
                }
                return prevActive;
            });
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isLoading]);

    const navItems = [
        { name: 'Home', id: 'home', icon: Home },
        { name: 'About', id: 'about', icon: User },
        { name: 'Experience', id: 'experience', icon: Clock },
        { name: 'Research', id: 'research', icon: Scroll },
        // { name: 'Testimonials', id: 'testimonials', icon: Quote }, // COMMENTED OUT
        { name: 'Education', id: 'education', icon: GraduationCap },
        { name: 'Projects', id: 'projects', icon: Briefcase },
        { name: 'Blog', id: 'blog', icon: BookOpen },
        { name: 'Contact', id: 'contact', icon: Mail },
    ];

    const themeClasses = isDarkMode ? {
        bg: 'bg-gray-900', text: 'text-gray-100', textMuted: 'text-gray-400', cardBg: 'bg-gray-800', border: 'border-gray-700', shadow: 'shadow-2xl shadow-gray-900/50'
    } : {
        bg: 'bg-gray-50', text: 'text-gray-900', textMuted: 'text-gray-600', cardBg: 'bg-white', border: 'border-gray-100', shadow: 'shadow-xl shadow-gray-200/50'
    };
      
    const particlesOptions = React.useMemo(() => particleConfig(isDarkMode), [isDarkMode]);

    return (
        <div className={`min-h-screen font-inter antialiased transition-colors duration-500 ${isDarkMode ? 'dark bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
            <AnimatePresence>
                {isLoading && <Preloader />}
            </AnimatePresence>
              
            <CustomCursor /> 
              
            {!isLoading && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.0, delay: 0.3 }}
                >
                    <motion.header 
                        initial={{ y: -100 }} 
                        animate={{ y: 0 }} 
                        transition={SPRING_CONFIG}
                        className={`${isDarkMode ? 'bg-gray-900/80' : 'bg-white/80'} backdrop-blur-md sticky top-0 z-50 py-3 transition-all duration-500 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}
                    >
                        <nav className="container mx-auto px-6 flex justify-between items-center">
                            <motion.div 
                                initial={{ opacity: 0, x: -20 }} 
                                animate={{ opacity: 1, x: 0 }} 
                                transition={{ delay: 0.2, ...SPRING_CONFIG }}
                                className="text-2xl font-bold text-blue-600"
                            >
                                <a href="#home" onClick={() => scrollToSection('home')} className="hover:text-blue-700 transition-colors">
                                    Masuddar.<span className='text-sm text-gray-400'>DS</span>
                                </a>
                            </motion.div>

                            <div className="hidden lg:flex items-center space-x-1">
                                {navItems.map(item => (
                                    <motion.button 
                                        key={item.id}
                                        onClick={() => scrollToSection(item.id)}
                                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                            activeSection === item.id 
                                            ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/20' 
                                            : 'text-gray-600 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400'
                                        }`}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        {item.name}
                                    </motion.button>
                                ))}
                                <div className="ml-4">
                                    <ThemeToggle isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
                                </div>
                            </div>

                            <div className="lg:hidden flex items-center space-x-3">
                                <ThemeToggle isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
                                <motion.button
                                    initial={{ opacity: 0, scale: 0.8 }} 
                                    animate={{ opacity: 1, scale: 1 }} 
                                    transition={{ delay: 0.5, ...SPRING_CONFIG }}
                                    className={`p-2 rounded-md ${themeClasses.text} hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-300`}
                                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                                >
                                    {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
                                </motion.button>
                            </div>
                        </nav>

                        <AnimatePresence>
                            {isMenuOpen && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className={`lg:hidden ${themeClasses.cardBg} ${themeClasses.shadow} absolute top-[68px] left-0 w-full z-40 overflow-hidden transition-colors duration-500 border-b border-gray-800`}
                                >
                                    <div className="py-4">
                                        {navItems.map(item => (
                                            <a
                                                key={item.id}
                                                href={`#${item.id}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    scrollToSection(item.id);
                                                }}
                                                className={`flex items-center space-x-4 px-8 py-3 text-lg ${themeClasses.textMuted} hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-600 transition-colors ${
                                                    activeSection === item.id ? 'text-blue-600 font-semibold bg-blue-50 dark:bg-blue-900/20' : ''
                                                }`}
                                            >
                                                <item.icon size={20} />
                                                <span>{item.name}</span>
                                            </a>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.header>
                      
                    <main className="overflow-x-hidden">
                        <HomePage 
                            scrollToSection={scrollToSection} 
                            isDarkMode={isDarkMode} 
                            particlesInit={particlesInit} 
                            particlesOptions={particlesOptions} 
                        />
                        <AboutPage isDarkMode={isDarkMode} />
                        <ExperiencePage isDarkMode={isDarkMode} />
                        <ResearchPage isDarkMode={isDarkMode} /> 
                        {/* <TestimonialsPage isDarkMode={isDarkMode} /> */}
                        <EducationPage isDarkMode={isDarkMode} /> 
                        <ProjectsPage isDarkMode={isDarkMode} />
                        <BlogPage isDarkMode={isDarkMode} />
                        <ContactPage isDarkMode={isDarkMode} />
                    </main>

                    {/* --- UPDATED FOOTER START --- */}
                    <footer className={`py-12 border-t transition-colors duration-500 ${isDarkMode ? 'bg-gray-900 border-gray-800 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                        <div className="container mx-auto px-6 text-center">
                            
                            {/* Social Icons */}
                            <motion.div 
                                initial={{ opacity: 0, y: 20 }} 
                                whileInView={{ opacity: 1, y: 0 }} 
                                viewport={{ once: true, amount: 0.5 }} 
                                transition={{ duration: 0.6 }}
                                className="flex justify-center items-center space-x-8 mb-8"
                            >
                                {/* GitHub */}
                                <SocialLink href={PERSONAL_INFO.github} label="GitHub" icon={Github} />
                                
                                {/* LinkedIn */}
                                <SocialLink href={PERSONAL_INFO.linkedin} label="LinkedIn" icon={Linkedin} />
                                
                                {/* Email (Replaced Twitter) */}
                                <a 
                                    href={`mailto:${PERSONAL_INFO.email}`}
                                    className="text-gray-400 hover:text-blue-500 transition-colors duration-300"
                                    aria-label="Email"
                                >
                                    <Mail size={28} />
                                </a>

                                {/* Blog (Only if you have one) */}
                                <SocialLink href={PERSONAL_INFO.blogs} label="Blog" icon={BookOpen} />
                            </motion.div>

                            {/* Copyright Text */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }} 
                                whileInView={{ opacity: 1, y: 0 }} 
                                viewport={{ once: true, amount: 0.5 }} 
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="flex flex-col items-center gap-2"
                            >
                                <p className="text-lg font-medium">
                                    &copy; {new Date().getFullYear()} {PERSONAL_INFO.name}.
                                </p>
                                <p className={`text-sm ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                                    Built with React, Tailwind CSS & Framer Motion
                                </p>
                            </motion.div>
                        </div>
                    </footer>
                    {/* --- UPDATED FOOTER END --- */}

                    <ScrollToTopButton isDarkMode={isDarkMode} />
                      
                    <DotNav 
                        navItems={navItems} 
                        activeSection={activeSection} 
                        scrollToSection={scrollToSection} 
                    />
                </motion.div>
            )}
        </div>
    );
}

// ==========================================
// 13. UI COMPONENTS: NAVIGATION HELPERS
// ==========================================

const SocialLink = ({ href, label, icon: Icon }) => (
    <motion.a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="text-gray-400 hover:text-blue-500 transition-colors duration-300" 
        aria-label={label}
        whileHover={{ scale: 1.2, rotate: -5, y: -2 }}
        whileTap={{ scale: 0.9 }}
    >
        <Icon size={28} />
    </motion.a>
);

// ==========================================
// 14. UI COMPONENTS: SHARED HELPERS
// ==========================================

const SectionTitle = ({ title, accent, isDarkMode }) => (
    <motion.div 
        initial={{ opacity: 0, y: -30 }} 
        whileInView={{ opacity: 1, y: 0 }} 
        viewport={{ once: true, amount: 0.5 }} 
        transition={SPRING_CONFIG}
        className="text-center mb-16"
    >
        <p className="text-md font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-2 transition-colors duration-500">{accent}</p>
        <h2 className={`text-5xl font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'} transition-colors duration-500`}>
            {title}
        </h2>
    </motion.div>
);

const SkillTag = ({ children, isDarkMode, isLarge = false }) => (
    <span className={`${isLarge ? 'text-base px-4 py-1.5' : 'text-sm px-3 py-1'} font-medium rounded-full whitespace-nowrap transition-colors duration-500 ${
        isDarkMode ? 'bg-blue-900/50 text-blue-300 border border-blue-900' : 'bg-white text-blue-100 text-blue-800 border border-blue-200'
    }`}>
        {children}
    </span>
);

const ContactInfoItem = ({ icon: Icon, label, value, href, isDarkMode }) => (
    <motion.a 
        href={href} 
        target={href.startsWith('mailto:') ? '_self' : '_blank'}
        rel={href.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
        initial={{ opacity: 0, x: -20 }} 
        whileInView={{ opacity: 1, x: 0 }} 
        viewport={{ once: true, amount: 0.5 }} 
        transition={{ duration: 0.5, delay: 0.1 }}
        className={`flex items-center space-x-4 p-4 rounded-2xl transition-all duration-300 border border-transparent ${isDarkMode ? 'hover:bg-gray-700 hover:border-gray-600' : 'hover:bg-gray-50 hover:border-gray-200'}`}
    >
        <div className={`p-3 rounded-full flex items-center justify-center shadow-lg ${isDarkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
            <Icon size={24} />
        </div>
        <div className="flex flex-col">
            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</span>
            <span className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{value}</span>
        </div>
    </motion.a>
);

const StatCard = ({ icon: Icon, value, label, isDarkMode }) => (
    <div className={`p-4 rounded-xl shadow-inner ${isDarkMode ? 'bg-gray-700/50' : 'bg-blue-50/50'} border border-blue-200 dark:border-blue-900 transition-colors duration-500`}>
        <Icon size={24} className="text-blue-500 mb-2" />
        <p className="text-2xl font-bold text-blue-600">{value}</p>
        <p className={`text-xs uppercase font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{label}</p>
    </div>
);

const DetailBlock = ({ icon: Icon, title, content, isDarkMode }) => (
    <div>
        <h4 className={`font-semibold text-2xl mb-3 flex items-center space-x-3 text-blue-600`}>
            <Icon size={24} className='text-purple-500' />
            <span>{title}</span>
        </h4>
        <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} whitespace-pre-line`}>{renderText(content)}</p>
    </div>
);



// --- UPDATED COMPONENT: Skill Category Card (Glassmorphism) ---
const SkillCategoryCard = ({ icon: Icon, title, skills, isDarkMode, delay }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ delay: delay, ...SPRING_CONFIG }}
            whileHover={{ y: -5 }}
            className={`
                p-6 rounded-2xl border backdrop-blur-md transition-all duration-300
                ${isDarkMode 
                    ? "bg-gray-800/40 border-gray-700 hover:border-blue-500/30 hover:bg-gray-800/60 shadow-lg shadow-black/20" 
                    : "bg-white/80 border-gray-100 hover:border-blue-200 hover:shadow-xl shadow-gray-200/50"}
            `}
        >
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <div className={`p-3 rounded-xl ${isDarkMode ? "bg-blue-500/10 text-blue-400" : "bg-blue-50 text-blue-600"}`}>
                    <Icon size={24} />
                </div>
                <h4 className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                    {title}
                </h4>
            </div>

            {/* Skills List */}
            <div className="space-y-1">
                {skills.map((skill) => (
                    <SkillProgress 
                        key={skill.name} 
                        name={skill.name} 
                        proficiency={skill.proficiency} 
                        isDarkMode={isDarkMode} 
                    />
                ))}
            </div>
        </motion.div>
    );
};

// ==========================================
// 15. PAGE COMPONENT: HOME
// ==========================================

const HomePage = ({ scrollToSection, isDarkMode, particlesInit, particlesOptions }) => (
    <section id="home" className={`relative container mx-auto px-6 min-h-[calc(100vh-80px)] flex items-center justify-center transition-colors duration-500`}>
        {particlesInit && (
            <Particles
                id="tsparticles"
                options={particlesOptions}
                className="absolute inset-0 z-0"
            />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center w-full max-w-7xl mx-auto relative z-10">
            
            {/* Text Content */}
            <motion.div 
                initial={{ opacity: 0, x: -40 }} 
                animate={{ opacity: 1, x: 0 }} 
                transition={{ ...SPRING_CONFIG, duration: 1.0 }}
                className="order-2 lg:order-1 flex flex-col justify-center lg:pr-8"
            >
                <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">👋</span>
                    <span className={`text-sm font-semibold tracking-widest uppercase ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        Hello, I'm
                    </span>
                </div>

                {/* Name: Professional Gradient (Sky to Blue), not Neon */}
                <h1 className={`text-5xl lg:text-7xl xl:text-8xl font-bold tracking-tight mb-4 leading-[1.1] ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                    Masuddar <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-blue-600">
                        Rahaman
                    </span>
                </h1>
                
                {/* Role Title: Clean Mono/Slate color */}
                <h2 className={`text-2xl lg:text-3xl font-medium mb-6 flex items-center gap-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    <span className={`w-12 h-1 rounded-full inline-block ${isDarkMode ? 'bg-sky-500' : 'bg-blue-600'}`}></span>
                    Data Science Student
                </h2>
                
                {/* Bio: Softer text color for eye comfort */}
                <p className={`max-w-xl text-lg leading-relaxed mb-10 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    {renderText("I transform complex, raw data into **high-impact AI/ML-DL models** and strategic business insights. Let's build intelligence together.")}
                </p>

                {/* Buttons: Professional Deep Colors */}
                <div className="flex flex-wrap gap-4">
                    {/* Project Button: Deep Blue */}
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => scrollToSection('projects')}
                        className={`px-8 py-4 rounded-full font-bold text-base shadow-lg transition-all flex items-center gap-2 ${
                            isDarkMode 
                            ? 'bg-blue-600 text-white hover:bg-blue-500 shadow-blue-900/20' 
                            : 'bg-blue-700 text-white hover:bg-blue-800 shadow-blue-200'
                        }`}
                    >
                        <span>Projects</span>
                        <Briefcase size={20} />
                    </motion.button>

                    {/* Research Button: Dark Slate/Gray */}
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => scrollToSection('research')}
                        className={`px-8 py-4 rounded-full font-bold text-base shadow-lg transition-all flex items-center gap-2 ${
                            isDarkMode 
                            ? 'bg-slate-700 text-white hover:bg-slate-600 shadow-slate-900/20' 
                            : 'bg-slate-200 text-slate-800 hover:bg-slate-300 shadow-slate-200'
                        }`}
                    >
                        <span>Research</span>
                        <Scroll size={20} />
                    </motion.button>

                    {/* Resume Button: Bordered */}
                    <motion.a
                        href={PERSONAL_INFO.resumeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`px-8 py-4 rounded-full font-bold text-base border transition-all flex items-center gap-2 ${
                            isDarkMode 
                            ? 'border-slate-600 text-slate-300 hover:border-slate-400 hover:text-white' 
                            : 'border-slate-300 text-slate-700 hover:border-slate-500 hover:text-black'
                        }`}
                    >
                        <span>Resume</span>
                        <Download size={20} />
                    </motion.a>
                </div>
            </motion.div>
            
            {/* Image Section - No Box, Very Subtle Glow */}
            <motion.div 
                initial={{ opacity: 0, scale: 0.8, x: 40 }} 
                animate={{ opacity: 1, scale: 1, x: 0 }} 
                transition={{ delay: 0.2, duration: 1.2, ease: "easeOut" }}
                className="order-1 lg:order-2 flex justify-center lg:justify-end relative"
            >
                {/* Very Subtle Ambient Glow (Blue/Teal) */}
                <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full blur-[120px] -z-10 pointer-events-none opacity-40 ${isDarkMode ? 'bg-sky-900' : 'bg-blue-100'}`} />

                <img 
                    src="https://i.postimg.cc/7LbmqK2Q/10840624-A2B9-41BF-B95A-04EB1927E08C.jpg" 
                    alt="Masuddar Rahaman" 
                    // Clean Image without borders
                    className="rounded-[2.5rem] w-[350px] h-[350px] lg:w-[550px] lg:h-[550px] object-cover shadow-2xl transition-transform duration-500 hover:scale-[1.01]"
                />
            </motion.div>
        </div>
    </section>
);



// ==========================================
// 16. PAGE COMPONENT: ABOUT
// ==========================================



const AboutPage = ({ isDarkMode }) => {
    // Helper for tiny info cards within the bio
    const BioCard = ({ icon: Icon, title, items, colorClass }) => (
        <div className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-100'} hover:shadow-lg transition-all duration-300`}>
            <div className="flex items-center gap-3 mb-3">
                <div className={`p-2 rounded-lg ${colorClass} bg-opacity-10 text-opacity-100`}>
                    <Icon size={20} />
                </div>
                <h4 className={`font-bold text-lg ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{title}</h4>
            </div>
            <ul className="space-y-2">
                {items.map((item, idx) => (
                    <li key={idx} className={`text-sm flex items-start gap-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${colorClass.replace('text-', 'bg-')}`} />
                        <span>{renderText(item)}</span>
                    </li>
                ))}
            </ul>
        </div>
    );

    return (
        <section
            id="about"
            className={`py-20 md:py-32 ${isDarkMode ? "bg-gray-800" : "bg-white"} scroll-mt-20 transition-colors duration-500 overflow-hidden relative`}
        >
            {/* Background Decor */}
            <div className={`absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-[120px] opacity-20 pointer-events-none ${isDarkMode ? 'bg-blue-900' : 'bg-blue-100'}`} />

            <div className="container mx-auto px-6 max-w-7xl relative z-10">
                <SectionTitle
                    title="About Me"
                    accent="The Data Whisperer"
                    isDarkMode={isDarkMode}
                />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    
                    {/* LEFT COLUMN: Image & Quick Stats */}
                    <div className="lg:col-span-5 flex flex-col items-center">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            className="relative group w-full max-w-md"
                        >
                            {/* Decorative Border/Frame */}
                            <div className={`absolute -inset-1 rounded-[2.5rem] bg-gradient-to-tr from-blue-600 to-purple-600 opacity-70 blur-sm group-hover:opacity-100 transition duration-500`} />
                            
                            <img
                                src="https://i.postimg.cc/xd7g8LvG/532F822F-EB55-42BE-8AFF-800CA76E0A4D.jpg"
                                alt="Masuddar Rahaman"
                                className={`relative rounded-[2.2rem] w-full object-cover shadow-2xl border-4 ${isDarkMode ? 'border-gray-900' : 'border-white'}`}
                            />
                            
                            {/* Floating Badge */}
                            {/* <div className={`absolute -bottom-6 -right-6 p-4 rounded-2xl shadow-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} flex items-center gap-3`}>
                                <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full">
                                    <GraduationCap className="text-blue-600" size={24} />
                                </div>
                                <div>
                                    <p className={`text-xs font-bold uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pursuing</p>
                                    <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>BS Data Science @ IIT Madras</p>
                                </div>
                            </div> */}
                        </motion.div>
                    </div>

                    {/* RIGHT COLUMN: The Content */}
                    <div className="lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                        >
                            {/* Headline */}
                            <h3 className={`text-3xl md:text-4xl font-bold mb-6 leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                Turning raw data into <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-600">
                                    Scientifically Grounded Insights
                                </span>
                            </h3>

                            {/* Main Intro - FIXED: Wrapped in renderText */}
                            <p className={`text-lg mb-8 leading-relaxed ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                                {renderText(
                                    "I operate at the intersection of **Statistics**, **Machine Learning**, and **Scalable Engineering**. Currently building a strong foundation in algorithms and analytical reasoning through my **B.Tech in IT**."
                                )}
                            </p>

                            {/* The Two Focus Areas (Grid) */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                                <BioCard 
                                    icon={Briefcase}
                                    title="What I Do"
                                    colorClass="text-blue-500"
                                    items={[
                                        "Build **End-to-End ML Systems** (EDA to Deployment).",
                                        "Develop **Predictive & Detection Models** for real-world apps.",
                                        "Transform insights into interactive apps using **Streamlit**."
                                    ]}
                                />
                                <BioCard 
                                    icon={FlaskConical}
                                    title="Research Focus"
                                    colorClass="text-purple-500"
                                    items={[
                                        "**NLP Pipelines** (Embeddings, Model Behavior Analysis).",
                                        "**Deep Learning** architectures & controlled experiments.",
                                        "Applied research with **reproducible workflows**."
                                    ]}
                                />
                            </div>

                            {/* Motivation / Footer Quote */}
                            <div className={`pl-4 border-l-4 ${isDarkMode ? 'border-blue-500/30' : 'border-blue-500'}`}>
                                <p className={`italic font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    "Driven by a curiosity to create meaningful explanations and impactful solutions from complex datasets."
                                </p>
                            </div>

                        </motion.div>
                    </div>
                </div>

                {/* --- CORE COMPETENCIES SECTION --- */}
                <div className="mt-24">
                    <motion.h3
                        initial={{ opacity: 0, y: -20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className={`text-2xl font-bold text-center mb-12 ${isDarkMode ? "text-gray-100" : "text-gray-800"}`}
                    >
                        Technical Arsenal
                    </motion.h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 relative z-10">
                        <SkillCategoryCard
                            icon={Database}
                            title="Programming"
                            skills={skillsData.programming}
                            isDarkMode={isDarkMode}
                            delay={0}
                        />
                        <SkillCategoryCard
                            icon={Brain}
                            title="ML & Analysis"
                            skills={skillsData.ml_analysis}
                            isDarkMode={isDarkMode}
                            delay={0.1}
                        />
                        <SkillCategoryCard
                            icon={BarChart2}
                            title="Visualization"
                            skills={skillsData.tools_viz}
                            isDarkMode={isDarkMode}
                            delay={0.2}
                        />
                        <SkillCategoryCard
                            icon={Clock}
                            title="DevOps"
                            skills={skillsData.dev_ops}
                            isDarkMode={isDarkMode}
                            delay={0.3}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};


// ==========================================
// 17. PAGE COMPONENT: EXPERIENCE (UPDATED LAYOUT)
// ==========================================

// --- Sub-component: Experience Modal ---
const ExperienceModal = ({ data, onClose, isDarkMode }) => {
    
    // Helper to get static classes for Tailwind to detect
    const getThemeStyles = (theme) => {
        const styles = {
            orange: {
                badge: "bg-orange-600",
                button: "bg-orange-600 hover:bg-orange-700 shadow-orange-500/40"
            },
            cyan: {
                badge: "bg-cyan-600",
                button: "bg-cyan-600 hover:bg-cyan-700 shadow-cyan-500/40"
            },
            rose: {
                badge: "bg-rose-600",
                button: "bg-rose-600 hover:bg-rose-700 shadow-rose-500/40"
            },
            indigo: {
                badge: "bg-indigo-600",
                button: "bg-indigo-600 hover:bg-indigo-700 shadow-indigo-500/40"
            }
        };
        return styles[theme] || styles.indigo;
    };

    const themeStyles = getThemeStyles(data.theme);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
        >
            <motion.div
                layoutId={`exp-card-${data.id}`}
                className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl relative shadow-2xl overflow-hidden ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* HERO IMAGE */}
                <div className="relative h-64 w-full">
                    <img 
                        src={data.image} 
                        alt={data.title} 
                        className="w-full h-full object-cover" 
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    
                    <button 
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors backdrop-blur-md z-20"
                    >
                        <X size={24} />
                    </button>

                    <div className="absolute bottom-4 left-6 right-6 z-10">
                        {/* FIXED BADGE COLOR */}
                        <span className={`inline-block px-3 py-1 mb-2 rounded-full text-[10px] font-bold uppercase tracking-widest text-white ${themeStyles.badge}`}>
                            {data.type}
                        </span>
                        <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight shadow-sm">
                            {data.company}
                        </h2>
                    </div>
                </div>

                <div className="p-8">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className={`flex items-center gap-1 text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            <Clock size={14} /> {data.period}
                        </span>
                    </div>

                    <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{data.title}</h2>

                    <div className="mb-8">
                        <h4 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-3">Role Description</h4>
                        <p className={`text-base leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {renderText(data.description)}
                        </p>
                    </div>

                    {data.proofUrl && data.proofUrl !== "#" && (
                        <div className="mt-auto pt-6 border-t border-gray-200 dark:border-gray-700">
                             {/* FIXED BUTTON COLOR */}
                             <a 
                                href={data.proofUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-transform hover:scale-105 shadow-lg ${themeStyles.button}`}
                            >
                                <ExternalLink size={18} />
                                <span>View Certificate / Proof</span>
                            </a>
                        </div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};


const ExperiencePage = ({ isDarkMode }) => {
    const [selectedExperience, setSelectedExperience] = useState(null);

    return (
        <section id="experience" className={`py-20 md:py-32 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} scroll-mt-20 transition-colors duration-500`}>
            {/* UPDATED CONTAINER: 
                - Increased max-width to 'max-w-[100rem]' to accommodate padding + 4 cards.
                - Increased padding: 'px-8' (mobile), 'md:px-16' (tablet), 'lg:px-24' (desktop) for better spacing.
            */}
            <div className="container mx-auto px-8 md:px-16 lg:px-24 max-w-[100rem]">
                <SectionTitle title="Experience" accent="Professional Journey" isDarkMode={isDarkMode} />
                
                {/* GRID: lg:grid-cols-4 keeps the 4 items in a row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {experienceData.map((exp, index) => (
                        <ExperienceCard 
                            key={index} 
                            exp={exp} 
                            index={index} 
                            isDarkMode={isDarkMode} 
                            onClick={() => setSelectedExperience(exp)}
                        />
                    ))}
                </div>
            </div>

            {/* POPUP MODAL */}
            <AnimatePresence>
                {selectedExperience && (
                    <ExperienceModal 
                        data={selectedExperience} 
                        onClose={() => setSelectedExperience(null)} 
                        isDarkMode={isDarkMode} 
                    />
                )}
            </AnimatePresence>
        </section>
    );
};

// --- Sub-component: Experience Card ---
const ExperienceCard = ({ exp, index, isDarkMode, onClick }) => {
    
    const getThemeColor = (theme) => {
        const colors = {
            orange: "bg-orange-500 shadow-orange-500/40 text-white",
            cyan: "bg-cyan-500 shadow-cyan-500/40 text-white",
            rose: "bg-rose-500 shadow-rose-500/40 text-white",
            indigo: "bg-indigo-600 shadow-indigo-500/40 text-white",
        };
        return colors[theme] || colors.indigo;
    };

    const cardBg = isDarkMode ? 'bg-gray-800/80' : 'bg-white';
    const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-100';
    const badgeClass = getThemeColor(exp.theme);
    const btnColor = `text-${exp.theme}-500 hover:bg-${exp.theme}-50 dark:hover:bg-${exp.theme}-900/20`;

    return (
        <motion.div
            layoutId={`exp-card-${exp.id}`}
            onClick={onClick}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ delay: index * 0.1, ...SPRING_CONFIG }}
            whileHover={{ y: -8, scale: 1.02 }}
            className={`flex flex-col h-full rounded-[2rem] border ${borderColor} ${cardBg} shadow-2xl cursor-pointer overflow-hidden relative backdrop-blur-sm group`}
        >
            {/* 1. ROLE TYPE BANNER */}
            <div className={`px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-center shadow-lg ${badgeClass}`}>
                {exp.type}
            </div>

            {/* 2. IMAGE AREA (Full Bleed Style) */}
            <div className="relative h-48 w-full overflow-hidden">
                <img 
                    src={exp.image} 
                    alt={exp.company} 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${isDarkMode ? 'from-gray-800/90' : 'from-white/90'} to-transparent`} />
                
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/40 backdrop-blur-[2px]">
                    <span className="bg-white/90 text-black px-4 py-2 rounded-full text-xs font-bold flex items-center gap-2 transform translate-y-2 group-hover:translate-y-0 transition-transform">
                        <ArrowUpRight size={14} /> View Details
                    </span>
                </div>
            </div>

            {/* 3. CONTENT BODY */}
            <div className="p-6 pt-4 flex-grow flex flex-col relative z-10">
                <div className="mb-2 flex items-center gap-2">
                    <Briefcase size={14} className={`text-${exp.theme}-500`} />
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {exp.company}
                    </span>
                </div>

                <h4 className={`text-xl font-bold mb-3 leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'} group-hover:text-${exp.theme}-500 transition-colors`}>
                    {exp.title}
                </h4>

                <p className={`text-sm mb-4 leading-relaxed line-clamp-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {renderText(exp.description)}
                </p>

                <div className={`mt-auto pt-4 border-t ${borderColor} flex items-center justify-between`}>
                    <div className={`flex items-center space-x-2 text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <Clock size={14} />
                        <span>{exp.period}</span>
                    </div>

                    <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            if(exp.proofUrl && exp.proofUrl !== "#") window.open(exp.proofUrl, '_blank');
                            else onClick();
                        }}
                        className={`flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${btnColor}`}
                    >
                        <Award size={14} />
                        <span>Proof</span>
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

// ==========================================
// 18. PAGE COMPONENT: RESEARCH
// ==========================================

const ResearchPage = ({ isDarkMode }) => {
    const [selectedResearch, setSelectedResearch] = useState(null);

    return (
        <section id="research" className={`py-20 md:py-32 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} scroll-mt-20 transition-colors duration-500`}>
            <div className="container mx-auto px-6 max-w-7xl">
                <SectionTitle title="Research" accent="Innovation & Impact" isDarkMode={isDarkMode} />
                  
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {researchData.map((item, index) => (
                        <ResearchCard 
                            key={item.id} 
                            data={item} 
                            index={index} 
                            isDarkMode={isDarkMode}
                            onClick={() => setSelectedResearch(item)} 
                        />
                    ))}
                </div>
            </div>

            {/* POPUP MODAL */}
            <AnimatePresence>
                {selectedResearch && (
                    <ResearchModal 
                        data={selectedResearch} 
                        onClose={() => setSelectedResearch(null)} 
                        isDarkMode={isDarkMode} 
                    />
                )}
            </AnimatePresence>
        </section>
    );
};

// --- Sub-component: Research Card ---
const ResearchCard = ({ data, index, isDarkMode, onClick }) => {
      
    // Theme Logic for "Showcase" Badge at the top
    const getShowcaseTheme = () => {
        if (data.showcaseStatus.includes('SBI')) return 'bg-purple-600 text-white shadow-purple-500/40';
        if (data.showcaseStatus.includes('Smart India')) return 'bg-amber-500 text-white shadow-amber-500/40';
        return 'bg-blue-600 text-white shadow-blue-500/40';
    };

    const showcaseTheme = getShowcaseTheme();
    const cardBg = isDarkMode ? 'bg-gray-800/80' : 'bg-white';
    const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-100';

    return (
        <motion.div
            layoutId={`card-${data.id}`}
            onClick={onClick}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ delay: index * 0.1, ...SPRING_CONFIG }}
            whileHover={{ y: -8, scale: 1.02 }}
            className={`flex flex-col h-full rounded-[2rem] border ${borderColor} ${cardBg} shadow-2xl cursor-pointer overflow-hidden relative backdrop-blur-sm group`}
        >
            {/* 1. SHOWCASE STATUS BANNER */}
            <div className={`px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-center shadow-lg ${showcaseTheme}`}>
                {data.showcaseStatus}
            </div>

            {/* 2. PROJECT IMAGE AREA */}
            <div className="relative h-48 w-full overflow-hidden">
                <img 
                    src={data.image} 
                    alt={data.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Gradient Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-t ${isDarkMode ? 'from-gray-800/90' : 'from-white/90'} to-transparent`} />
                  
                {/* Hover Overlay Text */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/40 backdrop-blur-[2px]">
                    <span className="bg-white/90 text-black px-4 py-2 rounded-full text-xs font-bold flex items-center gap-2 transform translate-y-2 group-hover:translate-y-0 transition-transform">
                        <ArrowUpRight size={14} /> View Details
                    </span>
                </div>
            </div>

            {/* Content Body */}
            <div className="p-8 pt-4 flex-grow flex flex-col relative z-10">
                <div className="mb-3 flex items-center gap-2">
                    <FlaskConical size={16} className="text-blue-500" />
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {data.type}
                    </span>
                </div>

                <h3 className={`text-xl font-bold mb-3 leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'} group-hover:text-blue-500 transition-colors`}>
                    {data.title}
                </h3>
                  
                <p className={`text-sm mb-6 leading-relaxed line-clamp-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {renderText(data.abstract)}
                </p>

                {/* Tech Tags */}
                <div className="flex flex-wrap gap-2 mt-auto mb-6">
                    {data.tags.slice(0, 3).map(tag => (
                        <span key={tag} className={`text-[10px] px-2 py-1 rounded-md font-medium border ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                            #{tag}
                        </span>
                    ))}
                </div>

                {/* Footer: Guide & LinkedIn */}
                {data.guide && (
                    <div className={`pt-5 border-t ${borderColor} flex items-center justify-between`}>
                        <div className="flex items-center gap-3">
                            <img 
                                src={data.guide.image} 
                                alt={data.guide.name} 
                                className="w-10 h-10 rounded-full object-cover border-2 border-transparent group-hover:border-blue-500 transition-all"
                            />
                            <div>
                                <p className={`text-[9px] uppercase font-bold text-gray-500 mb-0.5`}>Guided By</p>
                                <p className={`text-sm font-bold leading-none mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                    {data.guide.name}
                                </p>
                                <p className={`text-[9px] font-medium uppercase tracking-wide ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                    {data.guide.title.split('|')[0]}
                                </p>
                            </div>
                        </div>
                          
                        {/* LinkedIn Button */}
                        <a 
                            href={data.guide.profileUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            onClick={(e) => e.stopPropagation()} 
                            className={`p-2 rounded-full transition-colors z-30 ${isDarkMode ? 'bg-blue-900/20 text-blue-400 hover:bg-blue-600 hover:text-white' : 'bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white'}`}
                        >
                            <Linkedin size={18} />
                        </a>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

// --- Sub-component: Research Modal ---
const ResearchModal = ({ data, onClose, isDarkMode }) => {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
        >
            <motion.div
                layoutId={`card-${data.id}`}
                className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl relative shadow-2xl overflow-hidden ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* HERO IMAGE */}
                <div className="relative h-64 w-full">
                    <img src={data.image} alt={data.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <button 
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors backdrop-blur-md"
                    >
                        <X size={24} />
                    </button>
                    <div className="absolute bottom-4 left-6 right-6">
                        <span className={`inline-block px-3 py-1 mb-3 rounded-full text-[10px] font-bold uppercase tracking-widest ${data.showcaseStatus.includes('SBI') ? 'bg-purple-600 text-white' : 'bg-blue-600 text-white'}`}>
                            {data.showcaseStatus}
                        </span>
                        <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight shadow-sm">
                            {data.title}
                        </h2>
                    </div>
                </div>

                <div className="p-8">
                    {/* Full Abstract */}
                    <div className="mb-8">
                        <h4 className="text-lg font-bold mb-3 flex items-center gap-2">
                            <FileText size={20} className="text-purple-500" />
                            Abstract & Details
                        </h4>
                        <p className={`text-base leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {renderText(data.abstract)}
                        </p>
                    </div>

                    {/* Guide Info */}
                    <div className={`mb-8 flex items-center gap-4 p-4 rounded-2xl border ${isDarkMode ? 'border-gray-700/50 bg-gray-700/20' : 'border-gray-200 bg-gray-50'}`}>
                         <img src={data.guide.image} alt={data.guide.name} className="w-14 h-14 rounded-full object-cover" />
                         <div>
                            <p className="text-[10px] uppercase font-bold text-gray-500 mb-1">Guided By</p>
                            <p className="font-bold text-lg leading-none mb-1">{data.guide.name}</p>
                            <p className={`text-xs uppercase font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{data.guide.title}</p>
                         </div>
                         <a href={data.guide.profileUrl} target="_blank" rel="noopener noreferrer" className="ml-auto text-blue-500 hover:underline text-sm font-bold flex items-center gap-1">
                            <Linkedin size={16} /> Profile
                         </a>
                    </div>

                    {/* Full Tags */}
                    <div>
                        <h4 className="text-sm font-bold mb-3 uppercase tracking-wider text-gray-500">Technologies Used</h4>
                        <div className="flex flex-wrap gap-2">
                            {data.tags.map(tag => (
                                <span key={tag} className={`px-3 py-1.5 rounded-md font-medium border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-100 border-gray-200 text-gray-600'}`}>
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

// ==========================================
// 20. PAGE COMPONENT: EDUCATION
// ==========================================

// --- COMPONENT: Education Page (Container) ---
const EducationPage = ({ isDarkMode }) => {
    const sectionBg = isDarkMode ? 'bg-gray-800' : 'bg-white';
    const [showAllCerts, setShowAllCerts] = useState(false);
    const [showAllComps, setShowAllComps] = useState(false);

    const visibleCertifications = showAllCerts || certificationData.length <= CERTIFICATIONS_PER_PAGE
        ? certificationData
        : certificationData.slice(0, CERTIFICATIONS_PER_PAGE);

    const visibleCompetitions = showAllComps || competitionData.length <= COMPETITIONS_PER_PAGE
        ? competitionData
        : competitionData.slice(0, COMPETITIONS_PER_PAGE);

    return (
        <section id="education" className={`py-20 md:py-32 ${sectionBg} scroll-mt-20 transition-colors duration-500`}>
            <div className="container mx-auto px-6 max-w-7xl">
                <SectionTitle title="Education" accent="Degrees, Certifications & Achievements" isDarkMode={isDarkMode} />
                
                {/* --- ACADEMIC DEGREES SECTION --- */}
                <div className="max-w-5xl mx-auto mb-20"> 
                    <h3 className={`text-3xl font-bold mb-8 text-center ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Academic Degrees</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6"> 
                        {educationData.map((edu, index) => (
                            <AcademicCard key={index} edu={edu} delay={index * 0.1} isDarkMode={isDarkMode} />
                        ))}
                    </div>
                </div>

                {/* --- PROFESSIONAL CERTIFICATIONS SECTION (UPDATED) --- */}
                <div className="mt-24 max-w-6xl mx-auto">
                    <h3 className={`text-3xl font-bold mb-10 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'} flex items-center justify-center gap-3`}>
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <Award className="text-blue-600" size={28} />
                        </div>
                        <span>Professional Certifications</span>
                    </h3>
                    
                    <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {visibleCertifications.map((cert, index) => (
                            <CertificationCard 
                                key={index} 
                                cert={cert} 
                                isDarkMode={isDarkMode} 
                                delay={index * 0.1} 
                            />
                        ))}
                    </motion.div>

                    {certificationData.length > CERTIFICATIONS_PER_PAGE && (
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center mt-12"
                        >
                            <motion.button
                                onClick={() => setShowAllCerts(prev => !prev)}
                                className="px-8 py-3 rounded-full bg-blue-600 text-white font-bold shadow-lg shadow-blue-500/30 hover:bg-blue-700 hover:shadow-blue-500/50 transition-all duration-300 flex items-center mx-auto gap-2"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                role="button"
                            >
                                <span>{showAllCerts ? 'View Less' : `View All ${certificationData.length} Certifications`}</span>
                                {showAllCerts ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </motion.button>
                        </motion.div>
                    )}
                </div>

                {/* --- COMPETITIONS SECTION --- */}
                <div className="mt-24">
                    <h3 className={`text-3xl font-bold mb-10 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'} flex items-center justify-center gap-3`}>
                        <Trophy className="text-blue-600" size={30} />
                        <span>Data Science Competitions</span>
                    </h3>
                    <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {visibleCompetitions.map((comp, index) => (
                            <CompetitionCard 
                                key={index} 
                                comp={comp} 
                                isDarkMode={isDarkMode} 
                                delay={index * 0.1} 
                            />
                        ))}
                    </motion.div>
                    {competitionData.length > COMPETITIONS_PER_PAGE && (
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center mt-12"
                        >
                            <motion.button
                                onClick={() => setShowAllComps(prev => !prev)}
                                className="px-8 py-3 rounded-full bg-purple-600 text-white font-bold shadow-lg shadow-purple-500/30 hover:bg-purple-700 transition duration-300 flex items-center mx-auto space-x-2"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                role="button"
                            >
                                <span>{showAllComps ? 'View Less' : `View All ${competitionData.length} Competitions`}</span>
                                {showAllComps ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </motion.button>
                        </motion.div>
                    )}
                </div>
            </div>
        </section>
    );
};

// --- Sub-component: Academic Card ---
const AcademicCard = ({ edu, delay, isDarkMode }) => {
    const cardBg = isDarkMode ? 'bg-gray-800/80' : 'bg-white';
    const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-100';

    return (
        <motion.div
            layoutId={`edu-${edu.id}`}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ delay: delay, ...SPRING_CONFIG }}
            whileHover={{ y: -5, scale: 1.01 }}
            className={`flex flex-col md:flex-row h-full rounded-2xl border ${borderColor} ${cardBg} shadow-lg overflow-hidden relative backdrop-blur-sm group`}
        >
            {/* 1. PERIOD BANNER */}
            <div className={`absolute top-0 right-0 z-20 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-center shadow-sm bg-emerald-600 text-white rounded-bl-xl`}>
                {edu.period}
            </div>

            {/* 2. IMAGE AREA */}
            <div className="relative w-full md:w-48 h-48 md:h-auto flex-shrink-0 overflow-hidden bg-gray-200">
                <img 
                    src={edu.image} 
                    alt={edu.institution}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${isDarkMode ? 'from-gray-800/60' : 'from-white/60'} to-transparent md:bg-gradient-to-r`} />
            </div>

            {/* 3. CONTENT BODY */}
            <div className="p-6 flex-grow flex flex-col justify-center relative z-10">
                <div className="mb-2 flex items-center gap-2">
                    <GraduationCap size={16} className="text-blue-500" />
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Degree Program
                    </span>
                </div>

                <h3 className={`text-xl font-bold mb-1 leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'} group-hover:text-blue-500 transition-colors`}>
                    {edu.institution}
                </h3>
                
                <h4 className="text-sm font-semibold text-purple-500 mb-3">{edu.title}</h4>

                {/* CGPA Display */}
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-bold mb-4 w-fit ${isDarkMode ? 'bg-gray-700 text-emerald-400' : 'bg-emerald-50 text-emerald-700'}`}>
                    <Star size={14} className="fill-current" /> 
                    <span>CGPA: {edu.cgpa}</span>
                </div>

                {/* Bullet Points */}
                <ul className={`list-disc list-inside space-y-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {edu.details.map((point, index) => (
                        <li key={index}>{point}</li>
                    ))}
                </ul>
            </div>
        </motion.div>
    );
};

// --- Sub-component: Certification Card ---
const CertificationCard = ({ cert, isDarkMode, delay }) => {
    const cardBg = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
    const textMain = isDarkMode ? 'text-white' : 'text-gray-900';
    const textMuted = isDarkMode ? 'text-gray-400' : 'text-gray-600';

    return (
        <motion.div
            layout 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ delay: delay, ...SPRING_CONFIG }}
            whileHover={{ y: -8, boxShadow: "0 20px 40px -15px rgba(0,0,0,0.3)" }}
            className={`flex flex-col h-full rounded-2xl border ${cardBg} shadow-lg overflow-hidden group transition-all duration-300 relative`}
        >
            {/* 1. FULL VIEW IMAGE AREA */}
            <div className="relative h-48 w-full overflow-hidden bg-gray-100 dark:bg-gray-900">
                <img 
                    src={cert.image} 
                    alt={`${cert.name} Certificate`} 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/600x400/3b82f6/ffffff?text=Certificate"; }}
                />
                
                {/* Dark Gradient Overlay for depth */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60 transition-opacity group-hover:opacity-40" />

                {/* Provider Badge - Glassmorphism Style */}
                <div className="absolute top-3 left-3">
                    <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white bg-black/50 backdrop-blur-md border border-white/20 rounded-full shadow-sm">
                        {cert.provider}
                    </span>
                </div>
            </div>

            {/* 2. CONTENT BODY */}
            <div className="p-5 flex flex-col flex-grow relative z-10">
                <div className="mb-4">
                    <h4 className={`text-lg font-bold leading-tight mb-2 ${textMain} group-hover:text-blue-500 transition-colors`}>
                        {cert.name}
                    </h4>
                    
                    <div className={`flex items-center gap-2 text-xs font-medium ${textMuted}`}>
                        <Calendar size={14} className="text-blue-500" />
                        <span>Issued: {cert.date}</span>
                    </div>
                </div>

                {/* 3. ACTION BUTTON */}
                <div className="mt-auto">
                    <a 
                        href={cert.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={`group/btn flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-semibold transition-all duration-300
                            ${isDarkMode 
                                ? 'bg-gray-700 text-white hover:bg-blue-600' 
                                : 'bg-gray-100 text-gray-900 hover:bg-blue-600 hover:text-white'}
                        `}
                    >
                        <span>Verify Credential</span>
                        <ExternalLink size={16} className="transition-transform group-hover/btn:translate-x-1" />
                    </a>
                </div>
            </div>
        </motion.div>
    );
};

// --- Sub-component: Competition Card ---
const CompetitionCard = ({ comp, isDarkMode, delay }) => {
    // Theme Logic for "Showcase/Rank" Badge at the top
    const getRankTheme = () => {
        if (comp.rank.includes('1st')) return 'bg-amber-500 text-white shadow-amber-500/40';
        if (comp.rank.includes('Finalist')) return 'bg-purple-600 text-white shadow-purple-500/40';
        return 'bg-blue-600 text-white shadow-blue-500/40';
    };

    const rankTheme = getRankTheme();
    const cardBg = isDarkMode ? 'bg-gray-800/80' : 'bg-white';
    const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-100';

    return (
        <motion.div
            layout 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: delay, ...SPRING_CONFIG }}
            whileHover={{ y: -8, scale: 1.02 }}
            className={`flex flex-col h-full rounded-[2rem] border ${borderColor} ${cardBg} shadow-2xl cursor-pointer overflow-hidden relative backdrop-blur-sm group`}
        >
            {/* 1. RANK BANNER */}
            <div className={`px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-center shadow-lg ${rankTheme}`}>
                {comp.rank}
            </div>

            {/* 2. IMAGE AREA */}
            <div className="relative h-48 w-full overflow-hidden">
                <img 
                    src={comp.image} 
                    alt={`${comp.name} Award`} 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/400x300/a78bfa/ffffff?text=Competition+Trophy"; }}
                />
                {/* Gradient Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-t ${isDarkMode ? 'from-gray-800/90' : 'from-white/90'} to-transparent`} />
            </div>

            {/* 3. CONTENT BODY */}
            <div className="p-8 pt-4 flex-grow flex flex-col relative z-10">
                <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Trophy size={16} className="text-blue-500" />
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Competition
                        </span>
                    </div>
                </div>

                <h4 className={`text-xl font-bold mb-3 leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'} group-hover:text-blue-500 transition-colors`}>
                    {comp.name}
                </h4>

                <p className={`text-sm mb-4 leading-relaxed line-clamp-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {renderText(comp.details)}
                </p>

                {/* --- NEW: YouTube Demo Button --- */}
                {comp.videoUrl && (
                    <div className="mb-6">
                        <a 
                            href={comp.videoUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-md hover:bg-red-700 transition-colors transform hover:scale-105"
                            onClick={(e) => e.stopPropagation()} // Prevent card click
                        >
                            <Youtube size={18} fill="currentColor" />
                            <span>Watch Demo</span>
                        </a>
                    </div>
                )}

                {/* Footer: Date */}
                <div className={`mt-auto pt-5 border-t ${borderColor} flex items-center justify-between`}>
                    <div className={`flex items-center space-x-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <Calendar size={16} className="text-purple-500" />
                        <span>Date: {comp.date}</span>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

// ==========================================
// 21. PAGE COMPONENT: PROJECTS
// ==========================================

const ProjectsPage = ({ isDarkMode }) => {
    const [selectedProject, setSelectedProject] = useState(null);
    const [activeFilter, setActiveFilter] = useState('All');
    const [showAll, setShowAll] = useState(false);

    const filteredProjects = projectData.filter(p => activeFilter === 'All' || p.category === activeFilter);
    const visibleProjects = showAll || filteredProjects.length <= PROJECTS_PER_PAGE
        ? filteredProjects
        : filteredProjects.slice(0, PROJECTS_PER_PAGE);

    return (
        <section id="projects" className={`py-20 md:py-32 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} scroll-mt-20 transition-colors duration-500`}>
            <div className="container mx-auto px-6 max-w-7xl">
                <SectionTitle title="Projects" accent="Case Studies & Results" isDarkMode={isDarkMode} />

                <motion.div 
                    initial={{ opacity: 0, y: 20 }} 
                    whileInView={{ opacity: 1, y: 0 }} 
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ delay: 0.1, ...SPRING_CONFIG }}
                    className="flex flex-wrap justify-center gap-3 mb-16"
                >
                    {PROJECT_CATEGORIES.map(category => (
                        <motion.button
                            key={category}
                            onClick={() => { setActiveFilter(category); setShowAll(false); }}
                            className={`px-4 py-2 rounded-full font-semibold text-sm transition-colors duration-300 ${
                                activeFilter === category 
                                ? 'bg-blue-600 text-white shadow-md shadow-blue-500/30' 
                                : `${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`
                            }`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            role="button"
                        >
                            {category}
                        </motion.button>
                    ))}
                </motion.div>

                <motion.div 
                    layout
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
                >
                    {visibleProjects.map((project, index) => (
                        <ProjectGridCard 
                            key={project.id} 
                            project={project} 
                            onClick={() => setSelectedProject(project)} 
                            delay={index * 0.05}
                            isDarkMode={isDarkMode} 
                        />
                    ))}
                    {visibleProjects.length === 0 && (
                        <div className="col-span-full text-center py-10">
                            <p className={`text-xl font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`}>No projects found matching the category: **{activeFilter}**.</p>
                            <motion.button
                                onClick={() => setActiveFilter('All')}
                                className="mt-4 text-blue-600 hover:underline font-semibold"
                                role="button"
                            >
                                Show All Projects
                            </motion.button>
                        </div>
                    )}
                </motion.div>

                {filteredProjects.length > PROJECTS_PER_PAGE && (
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center mt-12"
                    >
                        {showAll ? (
                            <motion.button
                                onClick={() => setShowAll(false)}
                                className="px-8 py-3 rounded-full bg-purple-600 text-white font-bold shadow-lg shadow-purple-500/30 hover:bg-purple-700 transition duration-300 flex items-center mx-auto space-x-2"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                role="button"
                            >
                                <span>View Less Projects</span>
                                <ChevronUp size={20} />
                            </motion.button>
                        ) : (
                            <motion.button
                                onClick={() => setShowAll(true)}
                                className="px-8 py-3 rounded-full bg-purple-600 text-white font-bold shadow-lg shadow-purple-500/30 hover:bg-purple-700 transition duration-300 flex items-center mx-auto space-x-2"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                role="button"
                            >
                                <span>View All {filteredProjects.length} Projects</span>
                                <ChevronDown size={20} />
                            </motion.button>
                        )}
                          
                    </motion.div>
                )}
                {filteredProjects.length <= PROJECTS_PER_PAGE && activeFilter !== 'All' && projectData.length > PROJECTS_PER_PAGE && (
                    <p className={`text-center mt-6 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Showing all {filteredProjects.length} projects matching '{activeFilter}'.
                    </p>
                )}
            </div>
            <AnimatePresence>
                {selectedProject && (
                    <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} isDarkMode={isDarkMode} />
                )}
            </AnimatePresence>
        </section>
    );
};

// --- Sub-component: Project Grid Card ---
const ProjectGridCard = ({ project, onClick, delay, isDarkMode }) => {
    const cardClass = isDarkMode ? 'bg-gray-700 shadow-2xl shadow-black/50 border-gray-700' : 'bg-white shadow-xl shadow-gray-200/50 border-gray-100';
    
    // Check if we have a valid live link
    const hasLiveLink = project.liveUrl && project.liveUrl !== "#";

    return (
        <motion.div 
            layout
            initial={{ opacity: 0, scale: 0.8 }} 
            whileInView={{ opacity: 1, scale: 1 }} 
            viewport={{ once: true, amount: 0.3 }} 
            transition={{ delay: delay, ...SPRING_CONFIG }}
            whileHover={{ translateY: -10, boxShadow: "0 25px 50px rgba(59, 130, 246, 0.2)", scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`rounded-3xl overflow-hidden cursor-pointer border ${cardClass} flex flex-col transition-all duration-300`}
            onClick={onClick}
            role="button" 
        >
            {/* Image Section */}
            <div className="relative h-48 sm:h-56 bg-gray-100 flex items-center justify-center overflow-hidden">
                <img 
                    src={project.image || "https://placehold.co/600x400/cccccc/333333?text=No+Image"} 
                    alt={project.title} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-6">
                    <h3 className="text-2xl font-bold text-white leading-snug">{project.title}</h3>
                </div>
            </div>
            
            {/* Content Section */}
            <div className="p-6 flex-grow">
                <p className={`mb-4 text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{renderText(project.description)}</p>
                <div className="flex flex-wrap gap-2">
                    {project.tags.slice(0, 3).map(tag => <SkillTag key={tag} isDarkMode={isDarkMode}>{tag}</SkillTag>)}
                    {project.tags.length > 3 && <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>+{project.tags.length - 3} more</span>}
                </div>
            </div>

            {/* Footer Buttons */}
            <div className={`px-6 pb-6 pt-0 flex items-center justify-between mt-auto`}>
                <div className="flex gap-3">
                    {/* GitHub Button (Always Visible) */}
                    <a 
                        href={project.githubUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={`p-2 rounded-full transition-colors ${isDarkMode ? 'bg-gray-600 hover:bg-blue-600 text-white' : 'bg-gray-100 hover:bg-blue-600 hover:text-white text-gray-700'}`}
                        title="View Code"
                        onClick={(e) => e.stopPropagation()} 
                    >
                        <Github size={20} />
                    </a>

                    {/* Live Button (Only visible if link exists) */}
                    {hasLiveLink && (
                        <a 
                            href={project.liveUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`p-2 rounded-full transition-colors ${isDarkMode ? 'bg-gray-600 hover:bg-blue-600 text-white' : 'bg-gray-100 hover:bg-blue-600 hover:text-white text-gray-700'}`}
                            title="Live Demo"
                            onClick={(e) => e.stopPropagation()} 
                        >
                            <ExternalLink size={20} />
                        </a>
                    )}
                </div>

                <motion.span 
                    className="text-blue-500 font-medium flex items-center space-x-1 text-sm"
                    whileHover={{ x: 5 }}
                >
                    <span>Details</span> <ArrowUpRight size={16} />
                </motion.span>
            </div>
        </motion.div>
    );
};

// --- Sub-component: Project Modal ---
const ProjectModal = ({ project, onClose, isDarkMode }) => {
    const modalClass = isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900';
    const chartAxisColor = isDarkMode ? '#f3f4f6' : '#374151'; 
    
    // Check if we have a valid live link
    const hasLiveLink = project.liveUrl && project.liveUrl !== "#";

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ y: 50, opacity: 0, scale: 0.8 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={{ y: 50, opacity: 0, scale: 0.8 }}
                transition={{ type: "spring", stiffness: 100, damping: 20 }}
                className={`${modalClass} rounded-3xl shadow-2xl p-6 md:p-10 max-w-4xl w-full max-h-[95vh] overflow-y-auto relative transition-colors duration-500`}
                onClick={e => e.stopPropagation()}
            >
                <button 
                    onClick={onClose}
                    className={`absolute top-6 right-6 ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700`}
                >
                    <X size={28} />
                </button>
                <h3 className="text-4xl font-extrabold text-blue-600 mb-2">{project.title}</h3>
                <p className="text-xl font-light mb-8">{renderText(project.description)}</p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                    <StatCard icon={Zap} value={project.stats.linesOfCode} label="Codebase Size" isDarkMode={isDarkMode} />
                    <StatCard icon={Clock} value={project.stats.timeSpent} label="Development Time" isDarkMode={isDarkMode} />
                    <StatCard icon={Scale} value="Production" label="Deployment Status" isDarkMode={isDarkMode} />
                    <StatCard icon={TrendingUp} value={project.stats.impact} label="Quantified Impact" isDarkMode={isDarkMode} />
                </div>
                  
                <div className="space-y-8">
                    <DetailBlock icon={ArrowUpRight} title="The Problem" content={project.problem} isDarkMode={isDarkMode} />
                    <DetailBlock icon={Brain} title="My Approach & Methodology" content={project.methodology} isDarkMode={isDarkMode} />
                </div>

                {project.chartData && (
                    <div className={`mt-10 p-6 rounded-2xl ${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                        <h4 className={`font-semibold text-2xl mb-4 flex items-center space-x-2 text-blue-600`}><BarChart2 size={24} /><span>Key Results Visualization</span></h4>
                        <ResponsiveContainer width="100%" height={350}>
                            {project.chartType === 'bar' ? (
                                <BarChart data={project.chartData} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#e5e7eb'} />
                                    <XAxis dataKey="name" stroke={chartAxisColor} />
                                    <YAxis stroke={chartAxisColor} />
                                    <Tooltip contentStyle={{ borderRadius: '8px', background: isDarkMode ? '#1f2937' : '#ffffff', border: '1px solid #3b82f6' }} />
                                    <Legend />
                                    <Bar dataKey="accuracy" fill="#3b82f6" name="Accuracy" radius={[5, 5, 0, 0]} />
                                    <Bar dataKey="precision" fill="#10b981" name="Precision" radius={[5, 5, 0, 0]} />
                                </BarChart>
                            ) : (
                                <ScatterChart margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#e5e7eb'} />
                                    <XAxis type="number" dataKey="x" name="Feature 1" unit="" stroke={chartAxisColor} />
                                    <YAxis type="number" dataKey="y" name="Feature 2" unit="" stroke={chartAxisColor} />
                                    <ZAxis type="number" dataKey="z" range={[80, 500]} name="Feature 3 (Size)" />
                                    <Tooltip cursor={{ strokeDasharray: '3 3' }} contentStyle={{ borderRadius: '8px', background: isDarkMode ? '#1f2937' : '#ffffff', border: '1px solid #3b82f6' }} />
                                    <Legend />
                                    <Scatter name="Customer Segments" data={project.chartData} fill="#f59e0b" />
                                </ScatterChart>
                            )}
                        </ResponsiveContainer>
                    </div>
                )}
                  
                <div className="mt-8">
                    <h4 className={`font-semibold text-2xl mb-4 flex items-center space-x-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}><BookOpen size={24} /><span>Key Technologies</span></h4>
                    <div className="flex flex-wrap gap-3">
                        {project.tags.map(tag => <SkillTag key={tag} isDarkMode={isDarkMode} isLarge>{tag}</SkillTag>)}
                    </div>
                </div>

                <div className="flex flex-wrap gap-4 mt-10 justify-center">
                    <motion.a 
                        href={project.githubUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={`flex items-center space-x-2 px-8 py-4 rounded-full font-bold transition duration-300 ${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        role="button"
                    >
                        <Github size={20} />
                        <span>View Code</span>
                    </motion.a>
                    
                    {/* HIDE BUTTON IF LINK IS MISSING */}
                    {hasLiveLink && (
                        <motion.a 
                            href={project.liveUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="flex items-center space-x-2 bg-blue-600 text-white px-8 py-4 rounded-full font-bold hover:bg-blue-700 transition duration-300 shadow-lg shadow-blue-500/30"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            role="button"
                        >
                            <ExternalLink size={20} />
                            <span>Live Demo / App</span>
                        </motion.a>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

// ==========================================
// 22. PAGE COMPONENT: BLOG
// ==========================================

const BlogPage = ({ isDarkMode }) => {
    const validBlogData = blogData.filter(post => post.title);
    const featuredPost = validBlogData.length > 0 ? validBlogData[0] : null;
    const remainingPosts = validBlogData.length > 1 ? validBlogData.slice(1, 3) : [];

    return (
        <section id="blog" className={`py-20 md:py-32 ${isDarkMode ? 'bg-gray-900' : 'bg-white'} scroll-mt-20 transition-colors duration-500`}>
            <div className="container mx-auto px-6 max-w-7xl">
                <SectionTitle title="Blog" accent="Insights & Deep Dives" isDarkMode={isDarkMode} />
                  
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                    {featuredPost && (
                        <motion.a 
                            href={featuredPost.url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="md:col-span-3 block rounded-3xl overflow-hidden transition-all duration-300 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl dark:shadow-2xl dark:shadow-black/50"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.1 }}
                            transition={{ delay: 0.1, ...SPRING_CONFIG }}
                            whileHover={{ translateY: -8, boxShadow: "0 20px 40px rgba(59, 130, 246, 0.2)" }}
                            whileTap={{ scale: 0.98 }}
                            role="article"
                        >
                            <div className="md:flex">
                                <div className="md:w-1/2 p-6 md:p-8 flex flex-col justify-between">
                                    <div>
                                        <div className="flex justify-between items-center mb-3">
                                            <span className="text-sm text-blue-600 dark:text-blue-400 font-semibold">{featuredPost.readTime}</span>
                                            <span className="text-sm text-gray-500 dark:text-gray-400">{featuredPost.date}</span>
                                        </div>
                                        <h3 className="text-3xl font-bold my-3 leading-snug text-gray-800 dark:text-white">{featuredPost.title}</h3>
                                        <p className="mb-6 text-gray-600 dark:text-gray-300 text-lg">{featuredPost.snippet}</p>
                                    </div>
                                    <span className="text-blue-500 font-bold flex items-center space-x-2 text-lg">
                                        <span>Read Featured Article</span>
                                        <ArrowUpRight size={20} />
                                    </span>
                                </div>
                                <div className="md:w-1/2 h-64 md:h-auto bg-gray-100 dark:bg-gray-900 flex items-center justify-center overflow-hidden">
                                    <img 
                                        src={featuredPost.image} 
                                        alt={featuredPost.title} 
                                        className="w-full h-full object-cover"
                                        onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/800x600/3b82f6/ffffff?text=Blog+Image"; }}
                                    />
                                </div>
                            </div>
                        </motion.a>
                    )}

                    {remainingPosts.map((post, index) => (
                        <BlogCard 
                            key={index} 
                            post={post} 
                            delay={index * 0.1 + 0.2} 
                            isDarkMode={isDarkMode} 
                        />
                    ))}

                    <BlogPromoCard isDarkMode={isDarkMode} delay={0.4} />
                </div>

                <motion.div 
                    initial={{ opacity: 0, y: 20 }} 
                    whileInView={{ opacity: 1, y: 0 }} 
                    viewport={{ once: true, amount: 0.5 }} 
                    className="text-center mt-16"
                >
                    <motion.a 
                        href={PERSONAL_INFO.blogs} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="px-8 py-3 rounded-full bg-purple-600 text-white font-bold shadow-lg shadow-purple-500/30 hover:bg-purple-700 transition duration-300 flex items-center mx-auto space-x-2 w-fit"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        role="button"
                    >
                        <span>View All Blog Posts</span>
                        <ExternalLink size={20} />
                    </motion.a>
                </motion.div>
            </div>
        </section>
    );
};

// --- Sub-component: Blog Card ---
const BlogCard = ({ post, delay, isDarkMode }) => {
    const cardClass = isDarkMode ? 'bg-gray-800 shadow-2xl shadow-black/50 border-gray-700' : 'bg-white shadow-xl shadow-gray-200/50 border-gray-100';
    return (
        <motion.a 
            href={post.url} 
            target="_blank" 
            rel="noopener noreferrer" 
            initial={{ opacity: 0, y: 30 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true, amount: 0.3 }} 
            transition={{ delay: delay, ...SPRING_CONFIG }}
            whileHover={{ translateY: -8, boxShadow: "0 20px 40px rgba(59, 130, 246, 0.2)" }}
            whileTap={{ scale: 0.98 }}
            className={`md:col-span-1 block rounded-3xl overflow-hidden transition-all duration-300 border ${cardClass} flex flex-col`}
            role="article"
        >
            <div className="h-48 bg-gray-100 dark:bg-gray-900 flex items-center justify-center overflow-hidden">
                <img 
                    src={post.image} 
                    alt={post.title} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/600x400/cccccc/333333?text=Blog+Image"; }}
                />
            </div>
            <div className="p-6 flex flex-col flex-grow">
                <div className="flex-grow">
                    <div className="flex justify-between items-center mb-3">
                        <span className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} font-semibold`}>{post.readTime}</span>
                        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{post.date}</span>
                    </div>
                    <h3 className={`text-2xl font-bold my-3 leading-snug ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{post.title}</h3>
                    <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{post.snippet}</p>
                </div>
                <div className="pt-0 mt-auto">
                    <span className="text-blue-500 font-bold flex items-center space-x-2">
                        <span>Read Article</span>
                        <ArrowUpRight size={18} />
                    </span>
                </div>
            </div>
        </motion.a>
    );
};

// --- Sub-component: Blog Promo Card ---
function BlogPromoCard({ isDarkMode, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ delay: delay, ...SPRING_CONFIG }}
      whileHover={{ translateY: -6 }}
      className={`
        md:col-span-1 rounded-3xl p-6 flex flex-col text-center
        shadow-xl transition-all duration-500 border
        ${isDarkMode ? "bg-[#1B2431] border-gray-700" : "bg-white border-gray-200"}
      `}
    >
      <div className="w-full h-80 rounded-2xl overflow-hidden shadow-lg bg-gray-900/20 flex items-center justify-center">
        <img
          src="https://i.postimg.cc/7LbmqK2Q/10840624-A2B9-41BF-B95A-04EB1927E08C.jpg"
          alt="Masuddar Rahaman"
          className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-[1.05]"
        />
      </div>
      <h4 className={`text-3xl font-extrabold mt-6 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
        Coffee Together?
      </h4>
      <div className="flex flex-wrap justify-center gap-2 mt-3">
        <span className="px-3 py-1 text-sm rounded-full bg-emerald-600 text-white">Data Science</span>
        <span className="px-3 py-1 text-sm rounded-full bg-purple-600 text-white">AI</span>
        <span className="px-3 py-1 text-sm rounded-full bg-indigo-600 text-white">ML</span>
        <span className="px-3 py-1 text-sm rounded-full bg-cyan-600 text-white">DL</span>
      </div>
      <p className={`mt-4 mb-6 text-lg ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
        Let's build something meaningful together.
      </p>
      <div className="flex justify-center space-x-4">
        <motion.a
          href={PERSONAL_INFO.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center space-x-2 px-6 py-2 font-semibold rounded-full bg-blue-600 text-white hover:bg-blue-700"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Linkedin size={18} />
          <span>LinkedIn</span>
        </motion.a>
        <motion.a
          href={PERSONAL_INFO.github}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center space-x-2 px-6 py-2 font-semibold rounded-full bg-gray-800 text-gray-100 hover:bg-gray-700"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Github size={18} />
          <span>GitHub</span>
        </motion.a>
      </div>
    </motion.div>
  );
}

// ==========================================
// 23. PAGE COMPONENT: CONTACT
// ==========================================

const ContactPage = ({ isDarkMode }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [isCopied, setIsCopied] = useState(false); 

    const handleCopyEmail = () => {
        if (PERSONAL_INFO && PERSONAL_INFO.email) {
            navigator.clipboard.writeText(PERSONAL_INFO.email);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000); 
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);
          
        const form = e.target;
        const data = new FormData(form);

        try {
            // Ensure you update the Formspree URL in the fetch call if needed
            const response = await fetch("https://formspree.io/f/xjkvdzzp", {
                method: "POST",
                body: data,
                headers: { 'Accept': 'application/json' }
            });

            if (response.ok) {
                setSubmitStatus('success');
                form.reset();
            } else {
                setSubmitStatus('error');
            }
        } catch (error) {
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };
      
    const inputBg = isDarkMode ? 'bg-gray-900' : 'bg-gray-50';
    const inputBorder = isDarkMode ? 'border-gray-700 focus:border-blue-500' : 'border-gray-200 focus:border-blue-500';
    const inputText = isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400';
    const labelColor = isDarkMode ? 'text-gray-300' : 'text-gray-700';

    return (
        <section id="contact" className={`py-20 md:py-32 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} scroll-mt-20 transition-colors duration-500`}>
            <div className="container mx-auto px-6 max-w-6xl">
                <SectionTitle title="Contact" accent="Let's build something great" isDarkMode={isDarkMode} />
                
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20`}>
                    {/* Left Side: Info */}
                    <motion.div 
                        initial={{ opacity: 0, x: -30 }} 
                        whileInView={{ opacity: 1, x: 0 }} 
                        viewport={{ once: true, amount: 0.5 }} 
                        transition={{ delay: 0.2, ...SPRING_CONFIG }}
                    >
                        <h3 className={`text-3xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Let's Talk <span className="text-blue-600">Data</span>
                        </h3>
                        <p className={`text-lg mb-10 leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Got a challenging data problem, a research idea, or just want to connect? I'm always open to discussing new opportunities.
                        </p>
                        
                        <div className="space-y-6">
                            {/* --- CUSTOM EMAIL ITEM WITH COPY BUTTON --- */}
                            <div className={`flex items-center space-x-4 p-4 rounded-2xl border border-transparent transition-all duration-300 ${isDarkMode ? 'hover:bg-gray-700 hover:border-gray-600' : 'hover:bg-gray-50 hover:border-gray-200'}`}>
                                <div className={`p-3 rounded-full flex items-center justify-center shadow-lg ${isDarkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                                    <Mail size={24} />
                                </div>
                                <div className="flex-grow min-w-0"> 
                                    <span className={`block text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Email</span>
                                    
                                    <div className="flex items-center gap-3">
                                        <span className={`text-base font-bold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                            {PERSONAL_INFO.email}
                                        </span>
                                        
                                        <button 
                                            onClick={handleCopyEmail}
                                            className={`p-1.5 rounded-md transition-all flex-shrink-0 ${isDarkMode ? 'hover:bg-gray-600 text-gray-400 hover:text-white' : 'hover:bg-gray-200 text-gray-500 hover:text-black'}`}
                                            title="Copy Email"
                                            type="button"
                                        >
                                            {isCopied ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* LinkedIn & GitHub */}
                            <ContactInfoItem 
                                icon={Linkedin} 
                                label="LinkedIn" 
                                value="Connect with me" 
                                href={PERSONAL_INFO.linkedin} 
                                isDarkMode={isDarkMode} 
                            />
                            <ContactInfoItem 
                                icon={Github} 
                                label="GitHub" 
                                value="Explore my code" 
                                href={PERSONAL_INFO.github} 
                                isDarkMode={isDarkMode} 
                            />
                        </div>

                        <motion.a
                            href={PERSONAL_INFO.resumeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            download="Masuddar_Rahaman_Resume.pdf"
                            className={`mt-10 inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-base transition-all shadow-lg ${
                                isDarkMode 
                                ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-purple-500/30' 
                                : 'bg-gray-900 text-white hover:bg-black shadow-gray-900/30'
                            }`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            role="button"
                        >
                            <Download size={20} />
                            <span>Download Resume</span>
                        </motion.a>
                    </motion.div>

                    {/* Right Side: Form */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }} 
                        whileInView={{ opacity: 1, x: 0 }} 
                        viewport={{ once: true, amount: 0.5 }} 
                        transition={{ delay: 0.4, ...SPRING_CONFIG }}
                        className={`p-8 md:p-10 rounded-[2rem] border shadow-2xl ${isDarkMode ? 'bg-gray-900/50 border-gray-700 shadow-black/40' : 'bg-white border-gray-100 shadow-gray-200/50'}`}
                    >
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label htmlFor="name" className={`block text-sm font-semibold mb-2 ${labelColor}`}>Full Name</label>
                                <input 
                                    type="text" 
                                    name="name" 
                                    id="name" 
                                    required 
                                    className={`w-full px-5 py-4 rounded-xl border outline-none transition-all duration-300 focus:ring-4 focus:ring-blue-500/10 ${inputBg} ${inputBorder} ${inputText}`}
                                    placeholder="John Doe"
                                />
                            </div>
                            <div>
                                <label htmlFor="email" className={`block text-sm font-semibold mb-2 ${labelColor}`}>Email Address</label>
                                <input 
                                    type="email" 
                                    name="email" 
                                    id="email" 
                                    required 
                                    className={`w-full px-5 py-4 rounded-xl border outline-none transition-all duration-300 focus:ring-4 focus:ring-blue-500/10 ${inputBg} ${inputBorder} ${inputText}`}
                                    placeholder="john@example.com"
                                />
                            </div>
                            <div>
                                <label htmlFor="message" className={`block text-sm font-semibold mb-2 ${labelColor}`}>Message</label>
                                <textarea 
                                    id="message" 
                                    name="message" 
                                    rows="4" 
                                    required 
                                    className={`w-full px-5 py-4 rounded-xl border outline-none transition-all duration-300 focus:ring-4 focus:ring-blue-500/10 resize-none ${inputBg} ${inputBorder} ${inputText}`}
                                    placeholder="How can I help you?"
                                ></textarea>
                            </div>

                            <motion.button 
                                type="submit" 
                                className="w-full bg-blue-600 text-white px-6 py-4 rounded-xl font-bold hover:bg-blue-700 transition duration-300 shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2"
                                whileHover={{ scale: 1.02, y: -2 }}
                                whileTap={{ scale: 0.98 }}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="animate-spin" size={20} />
                                        <span>Sending...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>Send Message</span>
                                        <ArrowUpRight size={20} />
                                    </>
                                )}
                            </motion.button>

                            <AnimatePresence>
                                {submitStatus && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className={`overflow-hidden`}
                                    >
                                        <p className={`mt-4 text-center font-medium p-3 rounded-lg ${
                                            submitStatus === 'success' 
                                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' 
                                            : 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20'
                                        }`}>
                                            {submitStatus === 'success' ? 'Message sent! I will get back to you soon.' : 'Error sending message. Please try again.'}
                                        </p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </form>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
